import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AudioWaveform, Plus, Pencil, Trash2, Eye, Sparkles, Upload, Loader2, X, Lock, ExternalLink, Check, ImagePlus, Link2, Video, GripVertical, Tag, Search, HelpCircle, Languages, Undo2, Redo2, Building2, CheckCircle2, MessageSquare, Share2, Copy, Instagram, Linkedin, Facebook, Music2, Lightbulb, ScanSearch, CalendarRange,
  Star,
} from "lucide-react";
import { API, adminHeaders, mediaUrl } from "@/lib/api";
import { SECTIONS, sectionLabel, localize } from "@/lib/constants";
import ClientProfiles from "./ClientProfiles";
import ContentPlan from "./ContentPlan";
import { ArticleView } from "@/components/ArticleView";

const emptyI18n = () => ({ it: "", en: "" });
const newForm = () => ({
  id: null,
  title: emptyI18n(),
  body: emptyI18n(),
  meta_description: emptyI18n(),
  category: "articles",
  slug: "",
  cover_image_url: "",
  cover_alt: "",
  social_links: { youtube: "", vimeo: "", tiktok: "", instagram: "", soundcloud: "" },
  keyword: emptyI18n(),
  published: false,
  cover_brief: "",
  tenant: "spectraloom",
  author_name: "",
  author_title: "",
  author_photo_url: "",
});

const field = "w-full bg-[#0B0D12] border border-[#2D3748] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#8B5CF6] transition-colors";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-2";

const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ");
const wordCount = (s) => stripHtml(s).trim().split(/\s+/).filter(Boolean).length;
const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 60);

const computeSeo = (title, body, meta, keywords, cover, author = "") => {
  const t = (title || "").toLowerCase();
  const b = stripHtml(body).toLowerCase();
  const m = (meta || "").toLowerCase();
  const words = wordCount(body);
  const kwInText = (text, kw) => {
    const k = (kw || "").toLowerCase().trim();
    if (!k) return false;
    if (text.includes(k)) return true;
    const parts = k.split(/\s+/).filter((w) => w.length > 2);
    return parts.length > 0 && parts.every((w) => text.includes(w));
  };
  const anyKw = (text) => keywords.some((k) => kwInText(text, k));
  const hasKw = keywords.length > 0;
  const firstHeading = (body || "").match(/<h([1-6])/i);
  const au = (author || "").trim().toLowerCase();
  const authorOk = !!au && !["luca rossi", "nome autore", "autore"].includes(au);
  const checks = [
    { label: "Titolo 20–65 caratteri", ok: title.length >= 20 && title.length <= 65, hint: "Il titolo deve essere tra 20 e 65 caratteri. Accorcialo o allungalo per rientrare nel range ottimale." },
    { label: "Meta description 50–160 caratteri", ok: meta.length >= 50 && meta.length <= 160, hint: "La meta description deve essere tra 50 e 160 caratteri. Oltre i 160 caratteri Google la tronca automaticamente.", fix: "meta" },
    { label: "Almeno una keyword impostata", ok: hasKw, hint: "Imposta almeno una keyword principale prima di pubblicare l'articolo." },
    { label: "Keyword nel titolo", ok: hasKw && anyKw(t), hint: "Inserisci la keyword principale nel titolo." },
    { label: "Keyword nel corpo", ok: hasKw && anyKw(b), hint: "Inserisci la keyword principale nel corpo dell'articolo." },
    { label: "Keyword nella meta description", ok: hasKw && anyKw(m), hint: "Inserisci la keyword principale nella meta description." },
    { label: "Corpo ≥ 300 parole", ok: words >= 300, hint: "L'articolo è troppo corto. Aggiungi contenuto fino a raggiungere almeno 300 parole per essere indicizzato correttamente." },
    { label: "Almeno un sottotitolo (H2)", ok: /<h2/i.test(body || ""), hint: "Aggiungi almeno un sottotitolo H2 per strutturare l'articolo." },
    { label: "Immagine di copertina", ok: !!cover, hint: "Carica un'immagine di copertina con tag alt ottimizzato che contenga la keyword principale." },
    { label: "Tag H1 presente come primo heading", ok: !!firstHeading && firstHeading[1] === "1", hint: "Il primo heading deve essere H1. Modifica il tag iniziale da H2 a H1 con un titolo descrittivo di almeno 6 parole che contenga la keyword principale.", fix: "h1" },
    { label: "Nessun backtick nel testo", ok: !(body || "").includes("`"), hint: "Sono presenti backtick nel testo. Rimuovili e sostituiscili con testo normale o con il tag strong per le keyword importanti.", fix: "backtick" },
    { label: "Markup FAQ JSON-LD presente", ok: /faqpage/i.test(body || ""), hint: "Aggiungi il markup schema FAQPage in fondo all'articolo per abilitare i Featured Snippet su Google.", fix: "jsonld" },
    { label: "Scheda autore compilata", ok: authorOk, hint: "Inserisci nome e qualifica professionale dell'autore. Per articoli YMYL questo campo è obbligatorio per il punteggio EEAT di Google.", fix: "author" },
  ];
  const passed = checks.filter((c) => c.ok).length;
  return { score: Math.round((passed / checks.length) * 100), checks };
};

const keywordDensity = (body, keywords) => {
  const text = stripHtml(body).toLowerCase();
  const total = text.split(/\s+/).filter(Boolean).length;
  const kws = (keywords || []).map((k) => (k || "").toLowerCase().trim()).filter(Boolean);
  if (!kws.length || !total) return null;
  let best = { kw: kws[0], count: 0 };
  kws.forEach((kw) => {
    const re = new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
    const c = (text.match(re) || []).length;
    if (c > best.count) best = { kw, count: c };
  });
  const density = (best.count / total) * 100;
  let status = "ok";
  if (best.count === 0) status = "none";
  else if (density < 0.5) status = "low";
  else if (density > 2.5) status = "high";
  return { kw: best.kw, count: best.count, total, density, status };
};

const KW_GOALS = {
  easy: { wv: 0.35, label: "Vittorie facili", hint: "Premia la poca concorrenza (KD basso)" },
  balanced: { wv: 0.6, label: "Bilanciato", hint: "Equilibrio tra ricerche e concorrenza" },
  traffic: { wv: 0.85, label: "Grande traffico", hint: "Premia il volume di ricerche" },
};

export default function Admin({ adminKey }) {
  const [authState, setAuthState] = useState("checking");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("all");
  const [showClients, setShowClients] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [showChooser, setShowChooser] = useState(false);
  const [clients, setClients] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [libClient, setLibClient] = useState("");
  const dragIndex = useRef(null);
  const reorderable = filterCat === "all";

  const handleDragStart = (i) => { dragIndex.current = i; };
  const handleDragEnter = (i) => {
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(i, 0, moved);
      return updated;
    });
    dragIndex.current = i;
  };
  const handleDragEnd = async () => {
    dragIndex.current = null;
    try {
      await axios.post(`${API}/admin/content/reorder`, { ids: items.map((x) => x.id || x.slug) }, { headers: adminHeaders() });
      toast.success("Ordine salvato");
    } catch { toast.error("Salvataggio ordine fallito"); load(); }
  };

  useEffect(() => {
    axios.get(`${API}/admin/verify`, { params: { key: adminKey } })
      .then((r) => {
        if (r.data.valid) { sessionStorage.setItem("sl_admin_key", adminKey); setAuthState("ok"); }
        else setAuthState("denied");
      })
      .catch(() => setAuthState("denied"));
  }, [adminKey]);

  const load = useCallback(() => {
    const params = {};
    if (filterCat !== "all") params.category = filterCat;
    if (libClient) params.tenant = libClient;
    axios.get(`${API}/admin/content`, { headers: adminHeaders(), params })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Failed to load content"));
  }, [filterCat, libClient]);

  useEffect(() => {
    if (authState !== "ok") return;
    Promise.all([
      axios.get(`${API}/admin/clients`, { headers: adminHeaders() }).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/sectors`, { headers: adminHeaders() }).catch(() => ({ data: { macro: [] } })),
    ]).then(([c, s]) => { setClients(c.data || []); setSectors(s.data?.macro || []); });
  }, [authState]);

  useEffect(() => { if (authState === "ok") load(); }, [authState, load]);

  const clientByTenant = (t) => clients.find((c) => c.tenant === (t || "spectraloom"));
  const macroBySlug = (s) => sectors.find((m) => m.slug === s);
  const caseLabelForTenant = (t) => macroBySlug(clientByTenant(t)?.macro_sector)?.case_studies_label || sectionLabel("case_studies", "EN");
  const catLabelForItem = (item) => (item.category === "case_studies" ? caseLabelForTenant(item.tenant) : sectionLabel(item.category, "EN"));
  const libMacro = macroBySlug(clientByTenant(libClient)?.macro_sector);
  const tabLabel = (s) => (s.slug === "case_studies" && libClient ? (libMacro?.case_studies_label || s.en) : s.en);

  if (authState === "checking") {
    return <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center text-[#64748B]">Verifying…</div>;
  }
  if (authState === "denied") {
    return (
      <div className="min-h-screen bg-[#0B0D12] flex flex-col items-center justify-center gap-4 text-center px-6">
        <div className="w-14 h-14 rounded-2xl bg-[#121620] border border-[#2D3748] flex items-center justify-center">
          <Lock className="w-6 h-6 text-[#8B5CF6]" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-white">Access denied</h1>
        <p className="text-[#94A3B8] max-w-sm">Invalid admin key. Append the correct secret to the URL to access the panel.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D12]">
      <header className="border-b border-[#2D3748] bg-[#0B0D12]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#06B6D4] flex items-center justify-center">
              <AudioWaveform className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-heading text-lg font-bold text-white leading-none">Spectraloom</span>
              <span className="ml-2 text-xs font-mono text-[#06B6D4]">CMS</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="open-clients-button" onClick={() => setShowClients(true)} className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1.5">
              <Building2 className="w-4 h-4" /> Profili Cliente
            </button>
            <button data-testid="open-plan-button" onClick={() => setShowPlan(true)} className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1.5">
              <CalendarRange className="w-4 h-4" /> Piano editoriale
            </button>
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1.5">
              <ExternalLink className="w-4 h-4" /> View blog
            </a>
            <button
              data-testid="new-content-button"
              onClick={() => setShowChooser(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B5CF6] text-white text-sm font-semibold hover:bg-[#7C3AED] transition-colors"
            >
              <Plus className="w-4 h-4" /> New content
            </button>
          </div>
        </div>
      </header>

      {showClients && <ClientProfiles onClose={() => setShowClients(false)} />}
      {showChooser && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="content-chooser-modal">
          <div className="bg-[#121620] border border-[#2D3748] rounded-2xl w-full max-w-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#2D3748]">
              <h2 className="text-lg font-bold text-white">Cosa vuoi creare?</h2>
              <button data-testid="close-chooser" onClick={() => setShowChooser(false)} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              <button
                data-testid="choose-single-article"
                onClick={() => { setShowChooser(false); setEditing(newForm()); }}
                className="group text-left rounded-xl border border-[#2D3748] bg-[#0B0D12] p-5 hover:border-[#8B5CF6] hover:bg-[#8B5CF6]/5 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center mb-3">
                  <Pencil className="w-5 h-5 text-[#a78bfa]" />
                </div>
                <div className="text-sm font-bold text-white mb-1">Articolo singolo</div>
                <p className="text-[12px] text-[#94A3B8] leading-relaxed">Scrivi e pubblica subito un contenuto. Vai dritto all'editor con generazione AI, SEO e immagini.</p>
              </button>
              <button
                data-testid="choose-editorial-plan"
                onClick={() => { setShowChooser(false); setShowPlan(true); }}
                className="group text-left rounded-xl border border-[#2D3748] bg-[#0B0D12] p-5 hover:border-[#10B981] hover:bg-[#10B981]/5 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center mb-3">
                  <CalendarRange className="w-5 h-5 text-[#34d399]" />
                </div>
                <div className="text-sm font-bold text-white mb-1">Piano editoriale</div>
                <p className="text-[12px] text-[#94A3B8] leading-relaxed">Pianifica più idee data-driven (Volume + Punto d'oro), assegna date e stato nel calendario, esporta il PDF per il cliente.</p>
              </button>
            </div>
            <p className="px-6 pb-5 -mt-1 text-[11px] text-[#64748B]">Suggerimento: puoi sempre salvare un'idea nel piano con "+ Piano" o scriverla subito con "Usa questo" dal pannello Suggerisci argomenti.</p>
          </div>
        </div>
      )}
      {showPlan && (
        <ContentPlan
          onClose={() => setShowPlan(false)}
          onCreateArticle={(item) => {
            const idea = item.idea || {};
            const f = newForm();
            f.tenant = item.tenant || "spectraloom";
            f.title = { it: idea.title_it || idea.title || "", en: idea.title_en || "" };
            f.keyword = { it: idea.keyword_it || idea.keyword || "", en: idea.keyword_en || "" };
            setShowPlan(false);
            setEditing(f);
          }}
        />
      )}

      <main className="max-w-6xl mx-auto px-6 py-8">
        {editing ? (
          <Editor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h1 className="font-heading text-2xl font-bold text-white">Content library</h1>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  data-testid="library-client-filter"
                  value={libClient}
                  onChange={(e) => setLibClient(e.target.value)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#121620] text-[#94A3B8] border border-[#2D3748] hover:text-white focus:outline-none focus:border-[#8B5CF6]"
                >
                  <option value="">Tutti i clienti</option>
                  {clients.map((c) => <option key={c.tenant} value={c.tenant}>{c.name}{c.is_default ? " (predefinito)" : ""}</option>)}
                </select>
                <FilterChip active={filterCat === "all"} onClick={() => setFilterCat("all")} label="All" testid="all" />
                {SECTIONS.map((s) => (
                  <FilterChip key={s.slug} active={filterCat === s.slug} onClick={() => setFilterCat(s.slug)} label={tabLabel(s)} testid={s.slug} />
                ))}
              </div>
            </div>

            {reorderable && items.length > 1 && (
              <p data-testid="reorder-hint" className="mb-3 text-xs text-[#64748B] flex items-center gap-1.5">
                <GripVertical className="w-3.5 h-3.5" /> Trascina le righe per riordinare gli articoli. Il nuovo ordine viene salvato automaticamente.
              </p>
            )}

            <div data-testid="admin-content-list" className="rounded-2xl border border-[#2D3748] overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-[#121620] text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                <div className="col-span-5">Title</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-1">Lang</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {items.length === 0 ? (
                <div className="px-5 py-16 text-center text-[#64748B]">No content yet. Create your first piece.</div>
              ) : (
                items.map((item, i) => (
                  <Row
                    key={item.id || item.slug}
                    item={item}
                    index={i}
                    reorderable={reorderable}
                    categoryLabel={catLabelForItem(item)}
                    onDragStart={handleDragStart}
                    onDragEnter={handleDragEnter}
                    onDragEnd={handleDragEnd}
                    onEdit={() => setEditing({ ...newForm(), ...item, keyword: item.keyword_i18n || (typeof item.keyword === "string" && item.keyword ? { it: item.keyword, en: item.keyword } : (item.keyword && typeof item.keyword === "object" ? item.keyword : { it: "", en: "" })), cover_image_url: item.cover_image_url || "", cover_brief: "" })}
                    onChanged={load}
                  />
                ))
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const FilterChip = ({ active, onClick, label, testid }) => (
  <button
    data-testid={`admin-filter-${testid}`}
    onClick={onClick}
    className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-white text-[#0B0D12] border-white" : "bg-[#121620] text-[#94A3B8] border-[#2D3748] hover:text-white"}`}
  >
    {label}
  </button>
);

const Row = ({ item, index, reorderable, categoryLabel, onDragStart, onDragEnter, onDragEnd, onEdit, onChanged }) => {
  const rid = item.id || item.slug;
  const [dragging, setDragging] = useState(false);
  const hasIt = !!(item.title?.it);
  const hasEn = !!(item.title?.en);
  const langBadge = hasIt && hasEn ? "IT·EN" : hasIt ? "IT" : hasEn ? "EN" : "—";
  const togglePublish = async () => {
    try {
      await axios.put(`${API}/admin/content/${rid}`, { ...item, published: !item.published }, { headers: adminHeaders() });
      toast.success(!item.published ? "Published" : "Moved to draft");
      onChanged();
    } catch { toast.error("Update failed"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this content permanently?")) return;
    try {
      await axios.delete(`${API}/admin/content/${rid}`, { headers: adminHeaders() });
      toast.success("Deleted");
      onChanged();
    } catch { toast.error("Delete failed"); }
  };
  const previewUrl = async () => {
    const r = await axios.get(`${API}/admin/preview-token/${rid}`, { headers: adminHeaders() });
    return `${window.location.origin}/preview/${rid}?token=${r.data.token}`;
  };
  const openPreview = async () => {
    try { window.open(await previewUrl(), "_blank"); }
    catch { toast.error("Anteprima non disponibile"); }
  };
  const copyPreviewLink = async () => {
    try {
      const url = await previewUrl();
      try { await navigator.clipboard.writeText(url); toast.success("Link anteprima copiato"); }
      catch { window.prompt("Copia il link anteprima:", url); }
    } catch { toast.error("Link non disponibile"); }
  };
  const fb = item.client_feedback;
  const seoLang = item.title?.it ? "it" : "en";
  const seoKwStr = (item.keyword_i18n && item.keyword_i18n[seoLang]) || (typeof item.keyword === "string" ? item.keyword : "") || "";
  const seoScore = computeSeo(
    item.title?.[seoLang] || "",
    item.body?.[seoLang] || "",
    item.meta_description?.[seoLang] || "",
    seoKwStr.split(",").map((s) => s.trim()).filter(Boolean),
    item.cover_image_url,
    item.author_name || item.author || "",
  ).score;
  const seoColor = seoScore >= 80 ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : seoScore >= 50 ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-red-500/15 text-red-400 border-red-500/30";
  return (
    <div
      data-testid={`admin-row-${rid}`}
      draggable={reorderable}
      onDragStart={reorderable ? () => { setDragging(true); onDragStart(index); } : undefined}
      onDragEnter={reorderable ? () => onDragEnter(index) : undefined}
      onDragOver={reorderable ? (e) => e.preventDefault() : undefined}
      onDragEnd={reorderable ? () => { setDragging(false); onDragEnd(); } : undefined}
      className={`grid grid-cols-12 gap-4 px-5 py-4 border-t border-[#2D3748] items-center hover:bg-[#121620]/50 transition-opacity ${dragging ? "opacity-40" : "opacity-100"}`}
    >
      <div className="col-span-5 flex items-center gap-2 min-w-0">
        {reorderable && (
          <GripVertical data-testid={`drag-handle-${rid}`} className="w-4 h-4 shrink-0 text-[#475569] cursor-grab active:cursor-grabbing" />
        )}
        <span className="font-medium text-white truncate">{localize(item.title, "IT") || "Untitled"}</span>
        {fb && (fb.approved ? (
          <span data-testid={`feedback-badge-${rid}`} title={`Approvato da ${fb.name}${fb.comment ? " — " + fb.comment : ""}`}
            className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-emerald-500/15 text-emerald-400 border-emerald-500/30">
            <CheckCircle2 className="w-3 h-3" /> Approvato
          </span>
        ) : (
          <span data-testid={`feedback-badge-${rid}`} title={`Commento di ${fb.name}: ${fb.comment}`}
            className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-[#06B6D4]/15 text-[#67e8f9] border-[#06B6D4]/30">
            <MessageSquare className="w-3 h-3" /> Commento
          </span>
        ))}
        <span data-testid={`seo-badge-${rid}`} title={`Punteggio SEO: ${seoScore}/100`}
          className={`shrink-0 ml-auto px-2 py-0.5 rounded-full text-[11px] font-bold border font-mono ${seoColor}`}>
          SEO {seoScore}
        </span>
      </div>
      <div className="col-span-2 text-sm text-[#94A3B8]">{categoryLabel || sectionLabel(item.category, "EN")}</div>
      <div className="col-span-1 text-xs font-mono text-[#06B6D4]">{langBadge}</div>
      <div className="col-span-2">
        <button
          data-testid={`toggle-publish-${rid}`}
          onClick={togglePublish}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${item.published ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/30"}`}
        >
          {item.published ? "published" : "draft"}
        </button>
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1.5">
        <button data-testid={`preview-${rid}`} onClick={openPreview} title="Anteprima (come pubblicato)" className="p-2 rounded-lg text-[#94A3B8] hover:text-[#67e8f9] hover:bg-[#1A202C]"><Eye className="w-4 h-4" /></button>
        <button data-testid={`copy-link-${rid}`} onClick={copyPreviewLink} title="Copia link anteprima per il cliente" className="p-2 rounded-lg text-[#94A3B8] hover:text-[#a78bfa] hover:bg-[#1A202C]"><Link2 className="w-4 h-4" /></button>
        <button data-testid={`edit-${rid}`} onClick={onEdit} className="p-2 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#1A202C]"><Pencil className="w-4 h-4" /></button>
        <button data-testid={`delete-${rid}`} onClick={remove} className="p-2 rounded-lg text-[#94A3B8] hover:text-red-400 hover:bg-[#1A202C]"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const Editor = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [activeLang, setActiveLang] = useState("IT");
  const [undo, setUndo] = useState({});
  const [redo, setRedo] = useState({});
  const [mode, setMode] = useState("assisted");
  const [showPreview, setShowPreview] = useState(false);
  const [previewLang, setPreviewLang] = useState("IT");
  const [clients, setClients] = useState([]);
  const [sectors, setSectors] = useState([]);
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/clients`, { headers: adminHeaders() }).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/sectors`, { headers: adminHeaders() }).catch(() => ({ data: { macro: [] } })),
    ]).then(([c, s]) => { setClients(c.data || []); setSectors(s.data?.macro || []); });
  }, []);
  const [sourceText, setSourceText] = useState("");
  const [insertingImg, setInsertingImg] = useState(false);
  const bodyRef = useRef(null);
  const bodySelRef = useRef({ start: 0, end: 0 });
  const typingRef = useRef({ time: 0, lang: null });
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAuthor, setUploadingAuthor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genImages, setGenImages] = useState(false);
  const [genFree, setGenFree] = useState(false);
  const [buildingPrompt, setBuildingPrompt] = useState(false);
  const [pollPrompt, setPollPrompt] = useState(null);
  const [imageOptions, setImageOptions] = useState([]);
  const [imageSource, setImageSource] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [realKw, setRealKw] = useState(false);
  const [longtailKw, setLongtailKw] = useState(false);
  const [kwSort, setKwSort] = useState("pop");
  const [kwGoal, setKwGoalState] = useState(() => localStorage.getItem("absio_kw_goal") || "balanced");
  const setKwGoal = (g) => { setKwGoalState(g); localStorage.setItem("absio_kw_goal", g); setKwSort("gold"); };
  const [paaLoading, setPaaLoading] = useState(false);
  const [paaQuestions, setPaaQuestions] = useState({ it: [], en: [] });
  const [faqAnswering, setFaqAnswering] = useState(null);
  const [faqAnswers, setFaqAnswers] = useState({ it: {}, en: {} });
  const [faqOpen, setFaqOpen] = useState({ it: {}, en: {} });
  const [keywordSuggestions, setKeywordSuggestions] = useState({ it: [], en: [] });
  const [realVolumes, setRealVolumes] = useState({});
  const [volLoading, setVolLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [seoMarket, setSeoMarket] = useState("it");
  const [serpCompetitors, setSerpCompetitors] = useState({ it: [], en: [] });
  const [serpGaps, setSerpGaps] = useState({ it: [], en: [] });
  const [serpLoading, setSerpLoading] = useState(false);
  const [topicSeed, setTopicSeed] = useState("");
  const [topicIdeas, setTopicIdeas] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [topicBilingual, setTopicBilingual] = useState(false);
  const [relatedTerms, setRelatedTerms] = useState({ it: [], en: [] });
  const [suggestingTitle, setSuggestingTitle] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [titleListOpen, setTitleListOpen] = useState(false);
  const [suggestingMeta, setSuggestingMeta] = useState(false);
  const [suggestingSlug, setSuggestingSlug] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translatingBody, setTranslatingBody] = useState(false);
  const [translatingKw, setTranslatingKw] = useState(false);
  const [translatingMeta, setTranslatingMeta] = useState(false);
  const [injectingKw, setInjectingKw] = useState(false);
  const [injectingKwMeta, setInjectingKwMeta] = useState(false);
  const [kwPickerOpen, setKwPickerOpen] = useState(false);
  const [kwMetaPickerOpen, setKwMetaPickerOpen] = useState(false);
  const [kwBodyPickerOpen, setKwBodyPickerOpen] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [aiAudit, setAiAudit] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [socialPlatform, setSocialPlatform] = useState(null);
  const [socialBusy, setSocialBusy] = useState(false);
  const [socialResult, setSocialResult] = useState(null);
  const isEdit = !!initial.id;
  const lk = activeLang === "EN" ? "en" : "it";
  useEffect(() => { setSeoMarket(activeLang === "EN" ? "us" : "it"); }, [activeLang]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [k]: v } }));
  const setLang = (k, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [lk]: v } }));

  const snapshot = (fk, lang, val) => {
    setUndo((u) => {
      const key = `${fk}:${lang}`;
      const next = [...(u[key] || []), val];
      if (next.length > 60) next.shift();
      return { ...u, [key]: next };
    });
    setRedo((r) => { const key = `${fk}:${lang}`; if (!r[key]) return r; const n = { ...r }; delete n[key]; return n; });
  };
  const undoDepth = (fk) => (undo[`${fk}:${lk}`] || []).length;
  const redoDepth = (fk) => (redo[`${fk}:${lk}`] || []).length;
  const canUndo = (fk) => undoDepth(fk) > 0;
  const canRedo = (fk) => redoDepth(fk) > 0;
  const doUndo = (fk) => {
    const key = `${fk}:${lk}`;
    const stack = undo[key] || [];
    if (stack.length === 0) return;
    const prev = stack[stack.length - 1];
    const curVal = form[fk][lk];
    setForm((f) => ({ ...f, [fk]: { ...f[fk], [lk]: prev } }));
    setUndo((u) => {
      const s = (u[key] || []).slice(0, -1);
      const n = { ...u };
      if (s.length) n[key] = s; else delete n[key];
      return n;
    });
    setRedo((r) => { const next = [...(r[key] || []), curVal]; if (next.length > 60) next.shift(); return { ...r, [key]: next }; });
    toast.success("Modifica annullata");
  };
  const doRedo = (fk) => {
    const key = `${fk}:${lk}`;
    const stack = redo[key] || [];
    if (stack.length === 0) return;
    const nextVal = stack[stack.length - 1];
    const curVal = form[fk][lk];
    setForm((f) => ({ ...f, [fk]: { ...f[fk], [lk]: nextVal } }));
    setRedo((r) => {
      const s = (r[key] || []).slice(0, -1);
      const n = { ...r };
      if (s.length) n[key] = s; else delete n[key];
      return n;
    });
    setUndo((u) => { const next = [...(u[key] || []), curVal]; if (next.length > 60) next.shift(); return { ...u, [key]: next }; });
    toast.success("Modifica ripristinata");
  };
  const UndoBtn = ({ field }) => (canUndo(field) ? (
    <button type="button" data-testid={`undo-${field}`} onClick={() => doUndo(field)}
      title="Torna indietro di un passo (Ctrl+Z)"
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F59E0B] hover:text-[#fbbf24] transition-colors">
      <Undo2 className="w-3 h-3" />Annulla{undoDepth(field) > 1 ? ` (${undoDepth(field)})` : ""}
    </button>
  ) : null);
  const RedoBtn = ({ field }) => (canRedo(field) ? (
    <button type="button" data-testid={`redo-${field}`} onClick={() => doRedo(field)}
      title="Rifai l'ultima modifica annullata (Ctrl+Shift+Z)"
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#34D399] hover:text-[#6ee7b7] transition-colors">
      <Redo2 className="w-3 h-3" />Rifai{redoDepth(field) > 1 ? ` (${redoDepth(field)})` : ""}
    </button>
  ) : null);

  const curTitle = form.title[lk] || "";
  const curBody = form.body[lk] || "";
  const curMeta = form.meta_description[lk] || "";
  const kwFor = (key) => (form.keyword && typeof form.keyword === "object" ? (form.keyword[key] || "") : (form.keyword || ""));
  const curKeyword = kwFor(lk);
  const curKeywordSuggestions = keywordSuggestions[lk] || [];
  const goldScore = (kw) => {
    const rv = realVolumes[(kw.keyword || "").toLowerCase()];
    let volumeScore;
    if (rv && (rv.search_volume || 0) > 0) volumeScore = Math.min(100, Math.round(20 * Math.log10(rv.search_volume)));
    else volumeScore = kw.popularity || 0;
    let opportunity;
    if (rv && rv.competition_index != null) opportunity = 100 - rv.competition_index;
    else opportunity = kw.difficulty === "facile" ? 85 : kw.difficulty === "difficile" ? 25 : 55;
    const wv = KW_GOALS[kwGoal]?.wv ?? 0.6;
    return Math.round(wv * volumeScore + (1 - wv) * opportunity);
  };
  const realDiff = (kw) => {
    const rv = realVolumes[(kw.keyword || "").toLowerCase()];
    if (!rv || rv.competition_index == null) return null;
    const ci = rv.competition_index;
    const label = ci <= 33 ? "facile" : ci >= 67 ? "difficile" : "medio";
    return { ci, label };
  };
  const hasRealVol = (kw) => (realVolumes[(kw.keyword || "").toLowerCase()]?.search_volume || 0) > 0;
  const sortedSuggestions = kwSort === "gold"
    ? [...curKeywordSuggestions].sort((a, b) => (hasRealVol(b) - hasRealVol(a)) || (goldScore(b) - goldScore(a)))
    : curKeywordSuggestions;
  const recommendedKws = new Set([...curKeywordSuggestions].filter(hasRealVol)
    .sort((a, b) => goldScore(b) - goldScore(a)).slice(0, 3).map((k) => k.keyword));
  const curRelatedTerms = relatedTerms[lk] || [];
  const curPaaQuestions = paaQuestions[lk] || [];

  const selectedKeywords = curKeyword.split(",").map((s) => s.trim()).filter(Boolean);
  const kwCandidates = (() => {
    const out = []; const seen = new Set();
    const push = (s) => { const v = (s || "").trim(); if (v && !seen.has(v.toLowerCase())) { seen.add(v.toLowerCase()); out.push(v); } };
    selectedKeywords.forEach(push);
    curKeywordSuggestions.forEach((k) => push(k.keyword));
    return out;
  })();
  const toggleKeyword = (kw) => {
    const exists = selectedKeywords.includes(kw);
    const next = exists ? selectedKeywords.filter((k) => k !== kw) : [...selectedKeywords, kw];
    snapshot("keyword", lk, curKeyword);
    setLang("keyword", next.join(", "));
  };

  const useRecommendedAsMain = () => {
    const best = [...curKeywordSuggestions].filter(hasRealVol).sort((a, b) => goldScore(b) - goldScore(a))[0];
    if (!best) { toast.error("Nessuna keyword con dati reali: premi prima \"Volumi reali\""); return; }
    snapshot("keyword", lk, curKeyword);
    setLang("keyword", [best.keyword, ...selectedKeywords.filter((k) => k !== best.keyword)].join(", "));
    setKwSort("gold");
    toast.success(`Keyword principale: "${best.keyword}"`);
  };

  const suggestKeywords = async () => {
    if (!curTitle) { toast.error("Enter a title first"); return; }
    setSuggesting(true);
    try {
      const r = await axios.post(`${API}/admin/suggest-keywords`, { title: curTitle, language: activeLang, section: form.category, ...clientCtx() }, { headers: adminHeaders() });
      setKeywordSuggestions((s) => ({ ...s, [lk]: r.data.keywords || [] }));
      setRelatedTerms((s) => ({ ...s, [lk]: r.data.related || [] }));
      if (!r.data.keywords?.length) toast.error("No suggestions returned");
    } catch { toast.error("Keyword suggestion failed"); }
    finally { setSuggesting(false); }
  };

  const fetchRealVolumes = async () => {
    const kws = Array.from(new Set([
      ...(curKeywordSuggestions || []).map((k) => k.keyword),
      ...selectedKeywords,
      ...(curPaaQuestions || []),
    ].filter(Boolean))).slice(0, 200);
    if (!kws.length) { toast.error("Prima genera o seleziona delle keyword"); return; }
    setVolLoading(true);
    try {
      const r = await axios.post(`${API}/admin/seo/keyword-volume`, { keywords: kws, market: seoMarket }, { headers: adminHeaders() });
      const map = {};
      (r.data.results || []).forEach((x) => { if (x.keyword) map[x.keyword.toLowerCase()] = x; });
      setRealVolumes((prev) => ({ ...prev, ...map }));
      toast.success(`Volumi reali: ${r.data.market?.label || seoMarket}${r.data.cached ? " (cache)" : ""}`);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Errore DataForSEO";
      if (status === 402) {
        toast.error("⚠️ DataForSEO: credito esaurito. L'app continua con le stime AI. Ricarica il saldo su dataforseo.com per i volumi reali.", { duration: 8000 });
      } else if (status === 429) {
        toast.error("DataForSEO: troppe richieste, riprova tra poco");
      } else {
        toast.error(msg);
      }
    }
    finally { setVolLoading(false); }
  };

  const MARKET_LABELS = { it: "Italia", us: "USA", uk: "UK", es: "Spagna", fr: "Francia", de: "Germania" };
  const downloadSeoReport = async () => {
    const src = curKeywordSuggestions || [];
    if (!src.length) { toast.error("Prima genera delle keyword"); return; }
    const rows = src.map((kw) => {
      const rv = realVolumes[(kw.keyword || "").toLowerCase()] || {};
      return {
        keyword: kw.keyword,
        search_volume: rv.search_volume ?? null,
        cpc: rv.cpc ?? null,
        competition_index: rv.competition_index ?? null,
        popularity: kw.popularity ?? null,
        gold: goldScore(kw),
      };
    });
    setReportLoading(true);
    try {
      const r = await axios.post(`${API}/admin/seo/report`, {
        title: "Report Opportunità SEO",
        client_name: activeClient?.name || "",
        market_label: MARKET_LABELS[seoMarket] || seoMarket,
        market: seoMarket,
        source_label: activeClient?.data_source_label || "",
        goal_label: KW_GOALS[kwGoal]?.label || "",
        rows,
      }, { headers: adminHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "report-seo.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Report PDF generato");
    } catch { toast.error("Generazione report fallita"); }
    finally { setReportLoading(false); }
  };

  const realKeywords = async () => {
    const title = curTitle || form.title.it || form.title.en;
    if (!title && !(curKeyword || "").trim()) { toast.error("Inserisci prima un titolo o una keyword"); return; }
    setRealKw(true);
    try {
      const r = await axios.post(`${API}/admin/real-keywords`, { title: title || "", keyword: curKeyword, language: activeLang, section: form.category, ...clientCtx() }, { headers: adminHeaders() });
      setKeywordSuggestions((s) => ({ ...s, [lk]: r.data.keywords || [] }));
      setRelatedTerms((s) => ({ ...s, [lk]: r.data.related || [] }));
      if (!r.data.keywords?.length) toast.error("Nessuna keyword reale trovata");
      else toast.success("Keyword reali pronte (dati Google + DuckDuckGo)");
    } catch { toast.error("Ricerca keyword reali fallita"); }
    finally { setRealKw(false); }
  };

  const longtailKeywords = async () => {
    const title = curTitle || form.title.it || form.title.en;
    if (!title && !(curKeyword || "").trim()) { toast.error("Inserisci prima un titolo o una keyword"); return; }
    setLongtailKw(true);
    try {
      const r = await axios.post(`${API}/admin/longtail-keywords`, { title: title || "", keyword: curKeyword, language: activeLang, section: form.category, ...clientCtx() }, { headers: adminHeaders() });
      setKeywordSuggestions((s) => ({ ...s, [lk]: r.data.keywords || [] }));
      if (!r.data.keywords?.length) toast.error("Nessuna long-tail generata");
      else toast.success("Keyword long-tail generate dall'AI");
    } catch { toast.error("Generazione long-tail fallita"); }
    finally { setLongtailKw(false); }
  };

  const fetchQuestions = async () => {
    const title = curTitle || form.title.it || form.title.en;
    if (!title && !(curKeyword || "").trim()) { toast.error("Inserisci prima un titolo o una keyword"); return; }
    setPaaLoading(true);
    try {
      const r = await axios.post(`${API}/admin/real-questions`, { title: title || "", keyword: curKeyword, language: activeLang, section: form.category, ...clientCtx() }, { headers: adminHeaders() });
      setPaaQuestions((s) => ({ ...s, [lk]: r.data.questions || [] }));
      if (!(r.data.questions || []).length) toast.error("Nessuna domanda trovata");
      else toast.success("Domande reali pronte");
    } catch { toast.error("Ricerca domande fallita"); }
    finally { setPaaLoading(false); }
  };

  const generateAnswer = async (q, i) => {
    setFaqAnswering(`${lk}-${i}`);
    setFaqOpen((s) => ({ ...s, [lk]: { ...s[lk], [i]: true } }));
    try {
      const r = await axios.post(`${API}/admin/faq-answer`, { question: q, title: curTitle || form.title.it || form.title.en, keyword: curKeyword, language: activeLang, ...clientCtx() }, { headers: adminHeaders() });
      setFaqAnswers((s) => ({ ...s, [lk]: { ...s[lk], [i]: r.data?.answer || "" } }));
    } catch { toast.error("Generazione risposta fallita"); }
    finally { setFaqAnswering(null); }
  };
  const toggleFaq = (i) => setFaqOpen((s) => ({ ...s, [lk]: { ...s[lk], [i]: !s[lk]?.[i] } }));
  const setFaqText = (i, v) => setFaqAnswers((s) => ({ ...s, [lk]: { ...s[lk], [i]: v } }));
  const insertQA = (q, i) => {
    const ans = (faqAnswers[lk]?.[i] || "").trim();
    insertAtCursor(`\n<h2>${q}</h2>\n<p>${ans}</p>\n`);
    toast.success(ans ? "Domanda + risposta inserite nel corpo" : "Domanda inserita (risposta vuota)");
    setFaqOpen((s) => ({ ...s, [lk]: { ...s[lk], [i]: false } }));
  };

  const suggestTitle = async () => {
    const topic = curTitle || curKeyword;
    if (!topic) { toast.error("Enter a title or keyword first"); return; }
    setSuggestingTitle(true);
    try {
      const r = await axios.post(`${API}/admin/suggest-title`, { title: topic, keyword: curKeyword, language: activeLang, section: form.category, ...clientCtx() }, { headers: adminHeaders() });
      setTitleSuggestions(r.data.titles || []);
      setTitleListOpen(!!(r.data.titles || []).length);
      if (!r.data.titles?.length) toast.error("No title suggestions returned");
    } catch { toast.error("Title suggestion failed"); }
    finally { setSuggestingTitle(false); }
  };

  const autoTranslateTitleEN = async (itText) => {
    if (!itText || !itText.trim()) return;
    setTranslating(true);
    try {
      const r = await axios.post(`${API}/admin/translate`, { text: itText, source: "IT", target: "EN", keyword: kwFor("en") || kwFor("it") || null }, { headers: adminHeaders() });
      if (r.data?.text) {
        snapshot("title", "en", form.title.en);
        setForm((f) => ({ ...f, title: { ...f.title, en: r.data.text } }));
        toast.success("Titolo EN tradotto automaticamente");
      }
    } catch { /* traduzione silenziosa: non bloccare l'utente */ }
    finally { setTranslating(false); }
  };

  const translateBody = async () => {
    const source = activeLang;
    const target = source === "IT" ? "EN" : "IT";
    const srcKey = source === "IT" ? "it" : "en";
    const tgtKey = target === "IT" ? "it" : "en";
    const srcText = form.body[srcKey] || "";
    if (!srcText.trim()) { toast.error(`Scrivi prima il corpo in ${source}`); return; }
    if ((form.body[tgtKey] || "").trim()) {
      const ok = window.confirm(`Il corpo in ${target} è già compilato. Vuoi sovrascriverlo con la traduzione?`);
      if (!ok) return;
    }
    setTranslatingBody(true);
    try {
      const r = await axios.post(`${API}/admin/translate-body`, { text: srcText, source, target }, { headers: adminHeaders() });
      if (r.data?.text) {
        snapshot("body", tgtKey, form.body[tgtKey]);
        setForm((f) => ({ ...f, body: { ...f.body, [tgtKey]: r.data.text } }));
        toast.success(`Corpo tradotto in ${target}`);
      } else { toast.error("Traduzione non riuscita"); }
    } catch { toast.error("Traduzione del corpo non riuscita"); }
    finally { setTranslatingBody(false); }
  };

  const translateKeywords = async () => {
    const source = activeLang;
    const target = source === "IT" ? "EN" : "IT";
    const srcKey = source === "IT" ? "it" : "en";
    const tgtKey = target === "IT" ? "it" : "en";
    const srcText = kwFor(srcKey);
    if (!srcText.trim()) { toast.error(`Inserisci prima le keyword in ${source}`); return; }
    if (kwFor(tgtKey).trim()) {
      const ok = window.confirm(`Le keyword in ${target} sono già presenti. Vuoi sovrascriverle con la traduzione?`);
      if (!ok) return;
    }
    setTranslatingKw(true);
    try {
      const r = await axios.post(`${API}/admin/translate-keywords`, { text: srcText, source, target }, { headers: adminHeaders() });
      if (r.data?.text) {
        snapshot("keyword", tgtKey, kwFor(tgtKey));
        setForm((f) => ({ ...f, keyword: { ...f.keyword, [tgtKey]: r.data.text } }));
        toast.success(`Keyword tradotte in ${target}`);
      } else { toast.error("Traduzione non riuscita"); }
    } catch { toast.error("Traduzione delle keyword non riuscita"); }
    finally { setTranslatingKw(false); }
  };

  const translateMeta = async () => {
    const source = activeLang;
    const target = source === "IT" ? "EN" : "IT";
    const srcKey = source === "IT" ? "it" : "en";
    const tgtKey = target === "IT" ? "it" : "en";
    const srcText = form.meta_description[srcKey] || "";
    if (!srcText.trim()) { toast.error(`Scrivi prima la meta description in ${source}`); return; }
    if ((form.meta_description[tgtKey] || "").trim()) {
      const ok = window.confirm(`La meta description in ${target} è già presente. Vuoi sovrascriverla con la traduzione?`);
      if (!ok) return;
    }
    setTranslatingMeta(true);
    try {
      const r = await axios.post(`${API}/admin/translate-meta`, { text: srcText, source, target }, { headers: adminHeaders() });
      if (r.data?.text) {
        snapshot("meta_description", tgtKey, form.meta_description[tgtKey]);
        setForm((f) => ({ ...f, meta_description: { ...f.meta_description, [tgtKey]: r.data.text } }));
        toast.success(`Meta description tradotta in ${target}`);
      } else { toast.error("Traduzione non riuscita"); }
    } catch { toast.error("Traduzione della meta description non riuscita"); }
    finally { setTranslatingMeta(false); }
  };

  const applyTitleSuggestion = async (t) => {
    snapshot("title", lk, form.title[lk]);
    setLang("title", t);
    setTitleListOpen(false);
    toast.success("Titolo applicato");
    if (activeLang === "IT") await autoTranslateTitleEN(t);
  };

  const injectKeyword = async (kwArg) => {
    const kw = (kwArg !== undefined && kwArg !== null && kwArg !== "") ? String(kwArg) : (selectedKeywords[0] || curKeyword);
    if (!kw || !kw.trim()) { toast.error("Nessuna keyword disponibile: aggiungine una o generane"); return; }
    if (!(form.title[lk] || "").trim()) { toast.error("Scrivi prima un titolo"); return; }
    setKwPickerOpen(false);
    setInjectingKw(true);
    try {
      const r = await axios.post(`${API}/admin/inject-keyword`, { title: form.title[lk], keyword: kw.trim(), language: activeLang }, { headers: adminHeaders() });
      if (r.data?.title) {
        snapshot("title", lk, form.title[lk]);
        setForm((f) => ({ ...f, title: { ...f.title, [lk]: r.data.title } }));
        toast.success(`"${kw.trim()}" inserita nel titolo (${activeLang})`);
        if (activeLang === "IT" && !form.title.en) await autoTranslateTitleEN(r.data.title);
      }
    } catch { toast.error("Operazione fallita"); }
    finally { setInjectingKw(false); }
  };
  const injectKeywordManual = async () => {
    const kw = window.prompt("Keyword da inserire nel titolo (puoi modificarla):", selectedKeywords[0] || curKeyword);
    if (kw === null) return;
    injectKeyword(kw);
  };

  const injectKeywordMeta = async (kwArg) => {
    const kw = (kwArg !== undefined && kwArg !== null && kwArg !== "") ? String(kwArg) : (selectedKeywords[0] || curKeyword);
    if (!kw || !kw.trim()) { toast.error("Nessuna keyword disponibile: aggiungine una o generane"); return; }
    if (!(form.meta_description[lk] || "").trim()) { toast.error("Scrivi prima una meta description"); return; }
    setKwMetaPickerOpen(false);
    setInjectingKwMeta(true);
    try {
      const r = await axios.post(`${API}/admin/inject-keyword-meta`, { meta: form.meta_description[lk], keyword: kw.trim(), language: activeLang }, { headers: adminHeaders() });
      if (r.data?.meta_description) {
        snapshot("meta_description", lk, form.meta_description[lk]);
        setForm((f) => ({ ...f, meta_description: { ...f.meta_description, [lk]: r.data.meta_description } }));
        toast.success(`"${kw.trim()}" inserita nella meta (${activeLang})`);
      }
    } catch { toast.error("Operazione fallita"); }
    finally { setInjectingKwMeta(false); }
  };
  const injectKeywordMetaManual = async () => {
    const kw = window.prompt("Keyword da inserire nella meta description (puoi modificarla):", selectedKeywords[0] || curKeyword);
    if (kw === null) return;
    injectKeywordMeta(kw);
  };
  const insertKeywordBody = (kw) => {
    const v = (kw || "").trim();
    if (!v) { toast.error("Nessuna keyword disponibile"); return; }
    snapshot("body", lk, form.body[lk]);
    insertAtCursor(`<strong>${v}</strong>`);
    setKwBodyPickerOpen(false);
    toast.success(`"${v}" inserita nel corpo`);
  };
  const insertKeywordBodyManual = () => {
    const kw = window.prompt("Keyword da inserire nel corpo:", selectedKeywords[0] || curKeyword);
    if (kw === null) return;
    insertKeywordBody(kw);
  };
  const [justFixed, setJustFixed] = useState({});
  const [authorHi, setAuthorHi] = useState(false);
  const markFixed = (k) => setJustFixed((s) => ({ ...s, [k]: true }));
  const cleanBackticks = () => {
    if (!(curBody || "").includes("`")) return;
    snapshot("body", lk, form.body[lk]);
    const kwset = new Set(selectedKeywords.map((k) => k.toLowerCase()));
    const cleaned = form.body[lk]
      .replace(/`([^`]+)`/g, (m, w) => (kwset.has(w.trim().toLowerCase()) ? `<strong>${w}</strong>` : w))
      .replace(/`/g, "");
    setForm((f) => ({ ...f, body: { ...f.body, [lk]: cleaned } }));
    markFixed("backtick");
    toast.success("Backtick rimossi dal corpo");
  };
  const fixH1 = () => {
    const body = form.body[lk] || "";
    if (!/<h[2-6]/i.test(body)) { toast.error("Nessun sottotitolo da convertire in H1"); return; }
    snapshot("body", lk, body);
    const nb = body.replace(/<h([2-6])(\s[^>]*)?>([\s\S]*?)<\/h\1>/i, (m, lvl, attrs, inner) => `<h1${attrs || ""}>${inner}</h1>`);
    setForm((f) => ({ ...f, body: { ...f.body, [lk]: nb } }));
    markFixed("h1");
    toast.success("Primo heading convertito in H1");
  };
  const fixMeta = () => {
    const m = form.meta_description[lk] || "";
    if (m.length > 160) {
      snapshot("meta_description", lk, m);
      setForm((f) => ({ ...f, meta_description: { ...f.meta_description, [lk]: m.slice(0, 160).trim() } }));
      markFixed("meta");
      toast.success("Meta description troncata a 160 caratteri");
    } else {
      toast("Meta troppo corta: espandila fino ad almeno 50 caratteri", { icon: "ℹ️" });
    }
  };
  const fixJsonld = () => {
    const body = form.body[lk] || "";
    const faqs = [];
    const re = /<h2[^>]*>([\s\S]*?)<\/h2>([\s\S]*?)(?=<h2|<script|$)/gi;
    let m;
    while ((m = re.exec(body))) {
      const q = stripHtml(m[1]).trim();
      const a = stripHtml(m[2]).trim();
      if (q.endsWith("?") && a) faqs.push({ q, a });
    }
    if (!faqs.length) { toast.error("Nessuna FAQ trovata (serve una domanda in H2 che termina con '?')"); return; }
    const schema = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
    snapshot("body", lk, body);
    const script = `\n<script type="application/ld+json">${JSON.stringify(schema)}</script>`;
    setForm((f) => ({ ...f, body: { ...f.body, [lk]: body + script } }));
    markFixed("jsonld");
    toast.success(`Markup FAQPage inserito (${faqs.length} domande)`);
  };
  const fixAuthor = () => {
    setAuthorHi(true);
    setTimeout(() => setAuthorHi(false), 3000);
    const el = document.querySelector('[data-testid="input-author-name"]');
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); setTimeout(() => el.focus(), 400); }
    toast("Compila nome e qualifica dell'autore", { icon: "✍️" });
  };
  const seoFixers = { backtick: cleanBackticks, h1: fixH1, meta: fixMeta, jsonld: fixJsonld, author: fixAuthor };
  const distributeKeyword = async () => {
    if (!density || !density.kw) return;
    if (!(curBody || "").trim()) { toast.error("Il corpo è vuoto"); return; }
    setDistributing(true);
    try {
      const r = await axios.post(`${API}/admin/distribute-keyword`, { body: curBody, keyword: density.kw, language: activeLang, current: density.count, total: density.total }, { headers: adminHeaders() });
      if (r.data?.body) {
        snapshot("body", lk, form.body[lk]);
        setForm((f) => ({ ...f, body: { ...f.body, [lk]: r.data.body } }));
        toast.success("Keyword distribuita nel corpo");
      }
    } catch { toast.error("Distribuzione fallita"); }
    finally { setDistributing(false); }
  };

  const analyzeOptimize = async () => {
    if (!(curBody || "").trim()) { toast.error("Genera o scrivi prima l'articolo"); return; }
    setAuditing(true);
    setAiAudit(null);
    try {
      const r = await axios.post(`${API}/admin/analyze-optimize`, {
        body: curBody, keyword: curKeyword || "", language: activeLang, title: curTitle || "", meta: curMeta || "", ...clientCtx(),
      }, { headers: adminHeaders() });
      setAiAudit(r.data);
      setAuditOpen(true);
    } catch { toast.error("Analisi AI fallita"); }
    finally { setAuditing(false); }
  };

  const applyOptimized = () => {
    if (!aiAudit?.optimized_html) return;
    snapshot("body", lk, form.body[lk]);
    setForm((f) => ({ ...f, body: { ...f.body, [lk]: aiAudit.optimized_html } }));
    setAuditOpen(false);
    toast.success("Testo sostituito con la versione ottimizzata — punteggio SEO aggiornato");
  };

  const SOCIAL_PLATFORMS = [
    { id: "instagram", label: "Instagram", Icon: Instagram },
    { id: "linkedin", label: "LinkedIn", Icon: Linkedin },
    { id: "facebook", label: "Facebook", Icon: Facebook },
    { id: "tiktok", label: "TikTok", Icon: Music2 },
  ];

  const adaptForSocial = async (platform) => {
    if (!(curBody || "").trim()) { toast.error("Genera prima l'articolo"); return; }
    setSocialPlatform(platform);
    setSocialBusy(true);
    setSocialResult(null);
    try {
      const r = await axios.post(`${API}/admin/social-adapt`, {
        platform, body: curBody, title: curTitle || "", language: activeLang,
        url: `https://spectraloom.app/blog/${slug}`, ...clientCtx(),
      }, { headers: adminHeaders() });
      setSocialResult(r.data);
    } catch { toast.error("Adattamento social fallito"); }
    finally { setSocialBusy(false); }
  };

  const copySocialText = () => {
    if (!socialResult?.text) return;
    navigator.clipboard.writeText(socialResult.text);
    toast.success("Testo copiato");
  };

  const saveSocialDraft = async () => {
    if (!socialResult?.text) return;
    if (!initial.id) { toast.error("Salva prima l'articolo per conservare la bozza social"); return; }
    try {
      await axios.post(`${API}/admin/social-draft`, {
        article_id: initial.id, platform: socialPlatform, text: socialResult.text, score: socialResult.score,
      }, { headers: adminHeaders() });
      toast.success("Bozza social salvata");
    } catch { toast.error("Salvataggio bozza fallito"); }
  };

  const suggestMeta = async () => {
    if (!curTitle) { toast.error("Enter a title first"); return; }
    setSuggestingMeta(true);
    try {
      const r = await axios.post(`${API}/admin/suggest-meta`, { title: curTitle, keyword: curKeyword, language: activeLang, section: form.category, body: curBody, ...clientCtx() }, { headers: adminHeaders() });
      if (r.data.meta_description) { snapshot("meta_description", lk, form.meta_description[lk]); setLang("meta_description", r.data.meta_description); toast.success("Meta description generata"); }
      else toast.error("No meta returned");
    } catch { toast.error("Meta generation failed"); }
    finally { setSuggestingMeta(false); }
  };

  const generateForLang = async (langCode, kwOverride) => {
    const key = langCode === "EN" ? "en" : "it";
    const other = key === "en" ? "it" : "en";
    const title = form.title[key] || form.title[other];
    if (!title) { toast.error("Inserisci prima un titolo"); return false; }
    const kw = kwOverride || kwFor(key) || kwFor(other);
    if (!kw && !sourceText.trim()) { toast.error("Inserisci una keyword oppure incolla un testo/idea"); return false; }
    const r = await axios.post(`${API}/admin/generate`, {
      title, keyword: kw, language: langCode, section: form.category, mode,
      related: (relatedTerms[key] || relatedTerms[other] || []).length ? (relatedTerms[key] || relatedTerms[other]) : null,
      source_text: sourceText || "",
      market: seoMarket,
      competitors: (serpCompetitors[key] || []).length ? serpCompetitors[key] : undefined,
      content_gaps: (serpGaps[key] || []).length ? serpGaps[key] : undefined,
      ...clientCtx(),
    }, { headers: adminHeaders() });
    snapshot("body", key, form.body[key]);
    snapshot("meta_description", key, form.meta_description[key]);
    const comps = r.data.competitors || [];
    setSerpCompetitors((s) => ({ ...s, [key]: comps }));
    if (comps.length) toast.success(`${comps.length} concorrenti SERP analizzati per battere la classifica`);
    setForm((f) => ({
      ...f,
      body: { ...f.body, [key]: r.data.body },
      meta_description: { ...f.meta_description, [key]: f.meta_description[key] || r.data.meta_description },
    }));
    return { body: r.data.body || "", meta: r.data.meta_description || "" };
  };

  const suggestTopics = async () => {
    setTopicsLoading(true);
    setTopicsOpen(true);
    try {
      const r = await axios.post(`${API}/admin/seo/suggest-topics`, {
        seed: topicSeed || "", language: activeLang, section: form.category, market: seoMarket, bilingual: topicBilingual, ...clientCtx(),
      }, { headers: adminHeaders() });
      const ideas = r.data.ideas || [];
      setTopicIdeas(ideas);
      if (!ideas.length) toast.error("Nessun argomento generato, riprova");
      else toast.success(`${ideas.length} argomenti suggeriti${r.data.bilingual ? " (IT + EN)" : ""}${r.data.has_volumes ? " con volumi reali" : " (stime AI)"}`);
    } catch { toast.error("Suggerimento argomenti fallito"); }
    finally { setTopicsLoading(false); }
  };

  const applyTopic = (idea) => {
    const cacheVol = (kw, vol) => { if (kw && vol != null) setRealVolumes((rv) => ({ ...rv, [kw.toLowerCase()]: { search_volume: vol, cpc: null, competition_index: null } })); };
    if (idea.bilingual) {
      setForm((f) => ({
        ...f,
        title: { it: idea.title_it || f.title.it, en: idea.title_en || f.title.en },
        keyword: { ...(f.keyword && typeof f.keyword === "object" ? f.keyword : {}), it: idea.keyword_it || "", en: idea.keyword_en || "" },
      }));
      cacheVol(idea.keyword_it, idea.volume_it);
      cacheVol(idea.keyword_en, idea.volume_en);
      setTopicsOpen(false);
      toast.success("Argomento bilingue scelto: titolo e keyword IT + EN compilati");
      return;
    }
    setForm((f) => ({
      ...f,
      title: { ...f.title, [lk]: idea.title },
      keyword: { ...(f.keyword && typeof f.keyword === "object" ? f.keyword : {}), [lk]: idea.keyword },
    }));
    cacheVol(idea.keyword, idea.search_volume);
    setTopicsOpen(false);
    toast.success(`Argomento scelto: titolo e keyword compilati (${activeLang})`);
    if (activeLang === "IT" && !form.title.en) autoTranslateTitleEN(idea.title);
  };

  const addToPlan = async (idea) => {
    try {
      await axios.post(`${API}/admin/content-plan`, { tenant: form.tenant || "spectraloom", idea }, { headers: adminHeaders() });
      toast.success("Aggiunta al piano editoriale");
    } catch { toast.error("Salvataggio nel piano fallito"); }
  };

  const analyzeSerp = async () => {    const key = lk;
    const other = key === "en" ? "it" : "en";
    const kw = kwFor(key) || kwFor(other);
    if (!kw) { toast.error("Inserisci prima una keyword"); return; }
    setSerpLoading(true);
    try {
      const r = await axios.post(`${API}/admin/seo/analyze-serp`, {
        keyword: kw, language: activeLang, market: seoMarket,
      }, { headers: adminHeaders() });
      const comps = r.data.competitors || [];
      const gaps = r.data.gaps || [];
      setSerpCompetitors((s) => ({ ...s, [key]: comps }));
      setSerpGaps((s) => ({ ...s, [key]: gaps }));
      if (!comps.length) toast.error("Nessun concorrente SERP trovato (o credito DataForSEO esaurito)");
      else toast.success(`${comps.length} concorrenti · ${gaps.length} gap di contenuto individuati`);
    } catch { toast.error("Analisi SERP fallita"); }
    finally { setSerpLoading(false); }
  };

  const generate = async () => {
    if ((form.body[lk] || "").trim()) {
      const ok = window.confirm(`Il corpo in ${activeLang} è già presente. Rigenerando verrà sovrascritto. Continuare?`);
      if (!ok) return;
    }
    setGenerating(true);
    try {
      const ok = await generateForLang(activeLang);
      if (ok) toast.success(mode === "auto" ? "Article generated. Review and publish." : "Draft generated. Edit before publishing.");
    } catch { toast.error("Generation failed"); }
    finally { setGenerating(false); }
  };

  const generateBoth = async () => {
    if (!form.title.it && !form.title.en) { toast.error("Enter a title first"); return; }
    if (!kwFor("it") && !kwFor("en") && !sourceText.trim()) { toast.error("Enter a keyword first"); return; }
    setGenerating(true);
    try {
      // Allinea le keyword tra le due lingue: se una manca, la traduci dall'altra.
      // Così entrambe le lingue partono con la keyword impostata e i punteggi SEO restano vicini.
      let kwIt = kwFor("it");
      let kwEn = kwFor("en");
      if (kwIt && !kwEn) {
        try { const r = await axios.post(`${API}/admin/translate-keywords`, { text: kwIt, source: "IT", target: "EN" }, { headers: adminHeaders() }); kwEn = r.data?.text || kwIt; } catch (e) { kwEn = kwIt; }
      } else if (kwEn && !kwIt) {
        try { const r = await axios.post(`${API}/admin/translate-keywords`, { text: kwEn, source: "EN", target: "IT" }, { headers: adminHeaders() }); kwIt = r.data?.text || kwEn; } catch (e) { kwIt = kwEn; }
      }
      if (kwIt !== kwFor("it") || kwEn !== kwFor("en")) {
        if (kwIt !== kwFor("it")) snapshot("keyword", "it", kwFor("it"));
        if (kwEn !== kwFor("en")) snapshot("keyword", "en", kwFor("en"));
        setForm((f) => ({ ...f, keyword: { ...f.keyword, it: kwIt, en: kwEn } }));
      }
      const res = { it: null, en: null };
      try { res.it = await generateForLang("IT", kwIt); } catch (e) { /* keep null, handle below */ }
      try { res.en = await generateForLang("EN", kwEn); } catch (e) { /* keep null, handle below */ }
      // Assicura che la keyword compaia nel titolo di ciascuna lingua (ultimo punto SEO)
      const firstOf = (s) => (s || "").split(",")[0].trim();
      const kwItF = firstOf(kwIt);
      const kwEnF = firstOf(kwEn);
      let titleIt = form.title.it || "";
      let titleEn = form.title.en || "";
      if (titleIt && kwItF && !titleIt.toLowerCase().includes(kwItF.toLowerCase())) {
        try { const r = await axios.post(`${API}/admin/inject-keyword`, { title: titleIt, keyword: kwItF, language: "IT" }, { headers: adminHeaders() }); if (r.data?.title) titleIt = r.data.title; } catch (e) { /* ignore */ }
      }
      if (!titleEn && titleIt) {
        try { const r = await axios.post(`${API}/admin/translate`, { text: titleIt, source: "IT", target: "EN", keyword: kwEnF || null }, { headers: adminHeaders() }); if (r.data?.text) titleEn = r.data.text; } catch (e) { /* ignore */ }
      } else if (titleEn && kwEnF && !titleEn.toLowerCase().includes(kwEnF.toLowerCase())) {
        try { const r = await axios.post(`${API}/admin/inject-keyword`, { title: titleEn, keyword: kwEnF, language: "EN" }, { headers: adminHeaders() }); if (r.data?.title) titleEn = r.data.title; } catch (e) { /* ignore */ }
      }
      if (titleIt && titleIt !== (form.title.it || "")) snapshot("title", "it", form.title.it);
      if (titleEn && titleEn !== (form.title.en || "")) snapshot("title", "en", form.title.en);
      if (titleIt !== (form.title.it || "") || titleEn !== (form.title.en || "")) {
        setForm((f) => ({ ...f, title: { ...f.title, it: titleIt || f.title.it, en: titleEn || f.title.en } }));
      }
      const bodyOf = (k) => (res[k] && res[k].body) ? res[k].body : "";
      const metaOf = (k) => (res[k] && res[k].meta) ? res[k].meta : "";
      const bodySrc = ["it", "en"].find((k) => bodyOf(k));
      const metaSrc = ["it", "en"].find((k) => metaOf(k));
      if (!bodySrc) { toast.error("Generation failed"); return; }
      // Auto-fill any missing body/meta per language by translating from the language that has it
      const updates = { it: {}, en: {} };
      let didFallback = false;
      for (const k of ["it", "en"]) {
        if (!bodyOf(k) && bodySrc && bodySrc !== k) {
          try {
            const rb = await axios.post(`${API}/admin/translate-body`, { text: bodyOf(bodySrc), source: bodySrc.toUpperCase(), target: k.toUpperCase() }, { headers: adminHeaders() });
            if (rb.data?.text) { updates[k].body = rb.data.text; didFallback = true; }
          } catch (e) { /* leave empty */ }
        }
        if (!metaOf(k) && metaSrc && metaSrc !== k) {
          try {
            const rm = await axios.post(`${API}/admin/translate-meta`, { text: metaOf(metaSrc), source: metaSrc.toUpperCase(), target: k.toUpperCase() }, { headers: adminHeaders() });
            if (rm.data?.text) { updates[k].meta = rm.data.text; didFallback = true; }
          } catch (e) { /* leave empty */ }
        }
      }
      if (didFallback) {
        setForm((f) => {
          const body = { ...f.body };
          const meta = { ...f.meta_description };
          for (const k of ["it", "en"]) {
            if (updates[k].body) body[k] = updates[k].body;
            if (updates[k].meta) meta[k] = updates[k].meta;
          }
          return { ...f, body, meta_description: meta };
        });
        toast.success("Contenuto completato in IT ed EN (le parti mancanti sono state tradotte automaticamente).");
      } else {
        toast.success("Contenuto generato in IT ed EN.");
      }
    } finally { setGenerating(false); }
  };

  const generateImages = async () => {
    const title = curTitle || form.title.it || form.title.en;
    if (!title) { toast.error("Enter a title first"); return; }
    setGenImages(true); setImageOptions([]); setImageSource("ai");
    try {
      const r = await axios.post(`${API}/admin/generate-image`, { title, keyword: curKeyword || "", section: form.category, count: 3, brief: form.cover_brief || "", body: curBody || "", ...clientCtx() }, { headers: adminHeaders() });
      setImageOptions(r.data.images || []);
      toast.success("Cover proposals ready — pick one below.");
    } catch { toast.error("Image generation failed"); }
    finally { setGenImages(false); }
  };

  const preparePollinations = async () => {
    const title = curTitle || form.title.it || form.title.en;
    const body = curBody || form.body.it || form.body.en;
    if (!title && !body) { toast.error("Inserisci prima un titolo o del testo"); return; }
    setBuildingPrompt(true); setImageOptions([]); setImageSource("free");
    try {
      const r = await axios.post(`${API}/admin/pollinations-prompt`, { title: title || "", body: body || "", keyword: curKeyword, section: form.category, ...clientCtx() }, { headers: adminHeaders() });
      setPollPrompt(r.data?.prompt || "");
      toast.success("Prompt pronto — modificalo se vuoi, poi genera.");
    } catch { toast.error("Creazione prompt fallita"); }
    finally { setBuildingPrompt(false); }
  };

  const generateFree = async () => {
    if (!pollPrompt || !pollPrompt.trim()) { toast.error("Il prompt è vuoto"); return; }
    const title = curTitle || form.title.it || form.title.en;
    const body = curBody || form.body.it || form.body.en;
    setGenFree(true); setImageOptions([]); setImageSource("free");
    try {
      const r = await axios.post(`${API}/admin/generate-image-pollinations`, { title: title || "", body: body || "", keyword: curKeyword, section: form.category, count: 3, prompt: pollPrompt.trim(), ...clientCtx() }, { headers: adminHeaders() });
      const imgs = r.data.images || [];
      setImageOptions(imgs);
      if (!imgs.length) toast.error("Nessuna immagine generata");
      else toast.success("Proposte gratuite pronte — scegline una qui sotto.");
    } catch { toast.error("Generazione gratuita fallita, riprova"); }
    finally { setGenFree(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/upload`, fd, { headers: { ...adminHeaders() } });
      set("cover_image_url", r.data.url);
      toast.success("Cover uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const uploadAuthorPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAuthor(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/upload`, fd, { headers: { ...adminHeaders() } });
      set("author_photo_url", r.data.url);
      toast.success("Foto autore caricata");
    } catch { toast.error("Upload foto fallito"); }
    finally { setUploadingAuthor(false); e.target.value = ""; }
  };

  const insertAtCursor = (html) => {
    const ta = bodyRef.current;
    const cur = curBody || "";
    snapshot("body", lk, cur);
    let next;
    if (ta && typeof ta.selectionStart === "number") {
      const pos = ta.selectionStart;
      next = cur.slice(0, pos) + html + cur.slice(pos);
    } else {
      next = cur + html;
    }
    setLang("body", next);
  };

  const insertBodyImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setInsertingImg(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/upload`, fd, { headers: { ...adminHeaders() } });
      insertAtCursor(`\n<img src="${r.data.url}" alt="" style="width:100%;height:auto;border-radius:12px;margin:1.5rem 0" />\n`);
      toast.success("Immagine inserita nel corpo");
    } catch { toast.error("Upload immagine fallito"); }
    finally { setInsertingImg(false); e.target.value = ""; }
  };

  const getSelectedBodyText = () => {
    const ta = bodyRef.current;
    if (ta && typeof ta.selectionStart === "number" && ta.selectionEnd > ta.selectionStart) {
      return (curBody || "").slice(ta.selectionStart, ta.selectionEnd);
    }
    const { start, end } = bodySelRef.current || {};
    if (typeof start === "number" && end > start) {
      return (curBody || "").slice(start, end);
    }
    return "";
  };

  const replaceSelection = (html) => {
    const ta = bodyRef.current;
    const cur = curBody || "";
    let start, end;
    if (ta && typeof ta.selectionStart === "number" && ta.selectionEnd > ta.selectionStart) {
      start = ta.selectionStart; end = ta.selectionEnd;
    } else {
      const s = bodySelRef.current || {};
      if (typeof s.start === "number" && s.end > s.start) { start = s.start; end = s.end; }
    }
    if (start !== undefined) {
      snapshot("body", lk, cur);
      setLang("body", cur.slice(0, start) + html + cur.slice(end));
    } else {
      insertAtCursor(html);
    }
  };

  const insertExternalLink = () => {
    const sel = getSelectedBodyText();
    if (!sel) { toast.error("Seleziona prima la parola o la frase da trasformare in link"); return; }
    const url = window.prompt(`Inserisci l'URL per "${sel.slice(0, 40)}"`);
    if (!url) return;
    replaceSelection(`<a href="${url.trim()}" target="_blank" rel="noopener noreferrer">${sel}</a>`);
    toast.success(`Link aggiunto su "${sel.slice(0, 30)}"`);
  };

  const insertInternalLink = async () => {
    const sel = getSelectedBodyText();
    if (!sel) { toast.error("Seleziona prima la parola o la frase da trasformare in link"); return; }
    let list = [];
    try { const r = await axios.get(`${API}/admin/content`, { headers: adminHeaders() }); list = r.data || []; } catch { /* ignore */ }
    const options = list.map((a) => `• ${a.slug}`).join("\n") || "(nessun articolo trovato)";
    const slug = window.prompt(`Slug dell'articolo da collegare a "${sel.slice(0, 40)}":\n\nDisponibili:\n${options}`);
    if (!slug) return;
    replaceSelection(`<a href="https://spectraloom.app/blog/${slug.trim()}">${sel}</a>`);
    toast.success(`Link interno su "${sel.slice(0, 30)}"`);
  };

  const insertVideo = () => {
    const url = window.prompt("URL del video (YouTube o Vimeo)");
    if (!url) return;
    const yt = url.match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
    let embed;
    if (yt) embed = `https://www.youtube.com/embed/${yt[1]}`;
    else {
      const vm = url.match(/vimeo\.com\/(\d+)/);
      if (vm) embed = `https://player.vimeo.com/video/${vm[1]}`;
    }
    if (!embed) { toast.error("URL video non riconosciuto (usa YouTube o Vimeo)"); return; }
    insertAtCursor(`\n<div style="position:relative;width:100%;padding-bottom:56.25%;margin:1.5rem 0"><iframe src="${embed}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;border-radius:12px" allowfullscreen title="video"></iframe></div>\n`);
    toast.success("Video inserito");
  };

  const save = async (publish) => {
    if (!form.title.it && !form.title.en) { toast.error("Enter a title (IT or EN)"); return; }
    if (slugChangedWhilePublished && !window.confirm("Attenzione: cambiando lo slug di un articolo già pubblicato cambierà il suo URL pubblico e i link esistenti smetteranno di funzionare. Vuoi procedere?")) return;
    const payload = {
      title: form.title,
      body: form.body,
      meta_description: form.meta_description,
      category: form.category,
      slug: (form.slug || "").trim() || null,
      cover_image_url: form.cover_image_url || null,
      cover_alt: (form.cover_alt || "").trim(),
      social_links: form.social_links,
      keyword: [form.keyword?.it, form.keyword?.en].filter(Boolean).join(", "),
      keyword_i18n: { it: form.keyword?.it || "", en: form.keyword?.en || "" },
      published: publish,
      tenant: form.tenant || "spectraloom",
      author_name: (form.author_name || "").trim(),
      author_title: (form.author_title || "").trim(),
      author_photo_url: form.author_photo_url || null,
    };
    setSaving(true);
    try {
      if (isEdit) await axios.put(`${API}/admin/content/${initial.id}`, payload, { headers: adminHeaders() });
      else await axios.post(`${API}/admin/content`, payload, { headers: adminHeaders() });
      toast.success(publish ? "Published" : "Saved as draft");
      onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const seoAuthor = (form.author_name || "").trim() || ((form.tenant || "spectraloom") === "spectraloom" ? "Spectraloom Sound Engineers" : "");
  const seo = computeSeo(curTitle, curBody, curMeta, selectedKeywords, form.cover_image_url, seoAuthor);
  const density = keywordDensity(curBody, selectedKeywords);
  const seoColor = seo.score >= 80 ? "#10B981" : seo.score >= 50 ? "#F59E0B" : "#EF4444";
  const kwListFor = (key) => kwFor(key).split(",").map((s) => s.trim()).filter(Boolean);
  const seoIt = computeSeo(form.title.it || "", form.body.it || "", form.meta_description.it || "", kwListFor("it"), form.cover_image_url, seoAuthor);
  const seoEn = computeSeo(form.title.en || "", form.body.en || "", form.meta_description.en || "", kwListFor("en"), form.cover_image_url, seoAuthor);
  const seoGap = Math.abs(seoIt.score - seoEn.score);
  const seoLower = seoIt.score < seoEn.score ? "IT" : "EN";
  const activeClient = clients.find((c) => c.tenant === (form.tenant || "spectraloom"));
  const activeMacro = sectors.find((m) => m.slug === activeClient?.macro_sector);
  const activeSubName = activeMacro?.sub?.find((s) => s.slug === activeClient?.sub_sector)?.name || "";
  const clientCtx = () => ({
    brand: activeClient?.name || "",
    sector: activeMacro?.name || "",
    sub_sector: activeSubName,
    tone: activeClient?.tone_of_voice || "",
  });
  const caseStudiesLabel = activeMacro?.case_studies_label || sectionLabel("case_studies", "EN");
  const host = typeof window !== "undefined" ? window.location.host : "spectraloom.app";
  const slug = form.slug ? slugify(form.slug) : (slugify(curTitle) || "articolo");
  const slugChangedWhilePublished = isEdit && !!initial.published && !!(form.slug || "").trim() && slugify(form.slug) !== (initial.slug || "");

  const previewItem = {
    ...form,
    slug,
    keyword_i18n: form.keyword,
    published_at: initial.published_at || new Date().toISOString(),
  };
  const previewBrand = {
    name: activeClient?.name || "Spectraloom",
    logo_url: activeClient?.logo_url || "",
    is_default: activeClient?.is_default ?? ((form.tenant || "spectraloom") === "spectraloom"),
  };
  const openPreview = () => { setPreviewLang(activeLang); setShowPreview(true); };
  const copyPreviewLink = async () => {
    if (!isEdit || !initial.id) { toast.error("Salva prima l'articolo per generare il link condivisibile"); return; }
    try {
      const r = await axios.get(`${API}/admin/preview-token/${initial.id}`, { headers: adminHeaders() });
      const url = `${window.location.origin}/preview/${initial.id}?token=${r.data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Link anteprima copiato — invialo al cliente"); }
      catch { window.prompt("Copia il link anteprima:", url); }
    } catch { toast.error("Impossibile generare il link anteprima"); }
  };

  const suggestSlug = async () => {
    const title = curTitle || form.title.it || form.title.en;
    if (!title) { toast.error("Inserisci prima un titolo"); return; }
    setSuggestingSlug(true);
    try {
      const r = await axios.post(`${API}/admin/suggest-slug`, { title, keyword: curKeyword || "" }, { headers: adminHeaders() });
      if (r.data?.slug) { set("slug", r.data.slug); toast.success("Slug ottimizzato con AI"); }
      else toast.error("Nessuno slug suggerito");
    } catch { toast.error("Suggerimento slug fallito"); }
    finally { setSuggestingSlug(false); }
  };

  return (
    <div data-testid="content-editor" className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-white">{isEdit ? "Edit content" : "New content"}</h1>
        <div className="flex items-center gap-2">
          <button data-testid="preview-live-button" onClick={openPreview} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border border-[#06B6D4]/40 bg-[#06B6D4]/10 text-[#67e8f9] hover:bg-[#06B6D4]/20 transition-colors">
            <Eye className="w-4 h-4" /> Anteprima
          </button>
          <button data-testid="preview-link-button" onClick={copyPreviewLink} disabled={!isEdit} title={isEdit ? "Copia link condivisibile per il cliente" : "Salva prima l'articolo"} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#a78bfa] hover:bg-[#8B5CF6]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <Link2 className="w-4 h-4" /> Link anteprima
          </button>
          <button data-testid="close-editor" onClick={onClose} className="p-2 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#1A202C]"><X className="w-5 h-5" /></button>
        </div>
      </div>

      {showPreview && (
        <div data-testid="preview-modal" className="fixed inset-0 z-[60] bg-[#0B0D12] overflow-y-auto">
          <div className="sticky top-0 z-[61] bg-amber-500 text-[#0B0D12] text-xs font-bold py-1.5 flex items-center justify-center gap-3">
            <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> ANTEPRIMA LIVE — modifiche non ancora salvate</span>
            <button data-testid="close-preview" onClick={() => setShowPreview(false)} className="inline-flex items-center gap-1 rounded-full bg-[#0B0D12]/20 hover:bg-[#0B0D12]/30 px-2 py-0.5 text-[#0B0D12]"><X className="w-3.5 h-3.5" /> Chiudi</button>
          </div>
          <ArticleView item={previewItem} brand={previewBrand} caseLabel={activeMacro?.case_studies_label} lang={previewLang} onLangChange={setPreviewLang} mode="preview" />
        </div>
      )}

      {auditOpen && aiAudit && (
        <div data-testid="ai-audit-modal" className="fixed inset-0 z-[65] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8">
          <div className="w-full max-w-6xl max-h-[90vh] rounded-2xl border border-[#2D3748] bg-[#0B0D12] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#2D3748]">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#a78bfa]" />
                <h2 className="font-heading text-lg font-bold text-white">Analisi AI & Ottimizzazione SEO ({activeLang})</h2>
              </div>
              <button data-testid="ai-audit-close" onClick={() => setAuditOpen(false)} className="p-2 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#1A202C]"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto border-b lg:border-b-0 lg:border-r border-[#2D3748] space-y-5">
                <div className="flex flex-col items-center justify-center rounded-xl border border-[#2D3748] bg-[#121620] p-5">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Punteggio SEO AI</span>
                  <span data-testid="ai-audit-score" className="text-5xl font-extrabold font-mono mt-1"
                    style={{ color: aiAudit.score >= 80 ? "#10B981" : aiAudit.score >= 50 ? "#F59E0B" : "#EF4444" }}>
                    {aiAudit.score}<span className="text-2xl text-[#64748B]">/100</span>
                  </span>
                </div>
                <div>
                  <h3 className="flex items-center gap-1.5 text-sm font-bold text-red-400 mb-2"><X className="w-4 h-4" /> Problemi trovati ({aiAudit.analysis?.issues?.length || 0})</h3>
                  <ul data-testid="ai-audit-issues" className="space-y-1.5">
                    {(aiAudit.analysis?.issues || []).length === 0 && <li className="text-xs text-[#64748B] italic">Nessun problema rilevante.</li>}
                    {(aiAudit.analysis?.issues || []).map((it, i) => (
                      <li key={i} className="flex gap-2 text-xs text-[#CBD5E1]"><span className="text-red-400 mt-0.5">•</span><span>{String(it).replace(/\*\*/g, "")}</span></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="flex items-center gap-1.5 text-sm font-bold text-emerald-400 mb-2"><Check className="w-4 h-4" /> Punti di forza ({aiAudit.analysis?.strengths?.length || 0})</h3>
                  <ul data-testid="ai-audit-strengths" className="space-y-1.5">
                    {(aiAudit.analysis?.strengths || []).length === 0 && <li className="text-xs text-[#64748B] italic">—</li>}
                    {(aiAudit.analysis?.strengths || []).map((st, i) => (
                      <li key={i} className="flex gap-2 text-xs text-[#CBD5E1]"><span className="text-emerald-400 mt-0.5">•</span><span>{String(st).replace(/\*\*/g, "")}</span></li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="p-6 overflow-y-auto flex flex-col">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">Anteprima HTML ottimizzato</span>
                <div data-testid="ai-audit-optimized-preview" className="prose-sl flex-1 rounded-xl border border-[#2D3748] bg-white text-[#0B0D12] p-4 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: aiAudit.optimized_html || "" }} />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#2D3748]">
              <button data-testid="ai-audit-cancel" onClick={() => setAuditOpen(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#94A3B8] hover:text-white hover:bg-[#1A202C] transition-colors">Chiudi</button>
              <button data-testid="ai-audit-apply" onClick={applyOptimized} disabled={!aiAudit.optimized_html}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4] text-white text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-40">
                <Check className="w-4 h-4" /> Sostituisci testo originale
              </button>
            </div>
          </div>
        </div>
      )}

      {socialOpen && (
        <div data-testid="social-modal" className="fixed inset-0 z-[66] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8">
          <div className="w-full max-w-3xl max-h-[90vh] rounded-2xl border border-[#2D3748] bg-[#0B0D12] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#2D3748]">
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-[#a78bfa]" />
                <h2 className="font-heading text-lg font-bold text-white">Adatta per Social ({activeLang})</h2>
              </div>
              <button data-testid="social-close" onClick={() => setSocialOpen(false)} className="p-2 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#1A202C]"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Scegli la piattaforma</span>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {SOCIAL_PLATFORMS.map(({ id, label, Icon }) => (
                    <button key={id} data-testid={`social-platform-${id}`} disabled={socialBusy} onClick={() => adaptForSocial(id)}
                      className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-xs font-bold transition-colors disabled:opacity-40 ${socialPlatform === id ? "border-[#8B5CF6] bg-[#8B5CF6]/10 text-white" : "border-[#2D3748] bg-[#121620] text-[#CBD5E1] hover:border-[#8B5CF6]"}`}>
                      <Icon className="w-5 h-5" /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {socialBusy && (
                <div className="flex items-center justify-center gap-2 py-8 text-[#94A3B8] text-sm"><Loader2 className="w-5 h-5 animate-spin" /> Generazione in corso…</div>
              )}

              {socialResult && !socialBusy && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Anteprima post</span>
                    <span data-testid="social-score" className="text-sm font-bold font-mono"
                      style={{ color: socialResult.score >= 80 ? "#10B981" : socialResult.score >= 50 ? "#F59E0B" : "#EF4444" }}>
                      {socialResult.score}/100
                    </span>
                  </div>
                  <textarea data-testid="social-text" readOnly value={socialResult.text}
                    className="w-full h-56 rounded-xl border border-[#2D3748] bg-[#121620] text-sm text-[#E2E8F0] p-4 font-sans leading-relaxed resize-none" />
                  <ul data-testid="social-checks" className="space-y-1.5">
                    {socialResult.checks.map((c) => (
                      <li key={c.label} className="flex items-center gap-2 text-xs">
                        {c.ok ? <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <X className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                        <span className={c.ok ? "text-[#CBD5E1]" : "text-[#94A3B8]"}>{c.label}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button data-testid="social-copy" onClick={copySocialText} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#2D3748] text-sm font-semibold text-[#CBD5E1] hover:text-white hover:border-[#8B5CF6] transition-colors"><Copy className="w-4 h-4" /> Copia testo</button>
                    <button data-testid="social-save-draft" onClick={saveSocialDraft} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4] text-white text-sm font-bold hover:opacity-90 transition-opacity"><Check className="w-4 h-4" /> Salva come bozza social</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 rounded-xl border border-[#2D3748] bg-[#121620] p-4">
        <span className="text-sm text-[#94A3B8]">Compila entrambe le lingue — verranno salvate come <span className="text-white font-semibold">title/body/meta &#123;it, en&#125;</span> in un unico articolo</span>
        <div data-testid="editor-lang-tabs" className="flex items-center rounded-full border border-[#2D3748] p-1 bg-[#0B0D12]">
          {["IT", "EN"].map((l) => (
            <button key={l} data-testid={`editor-lang-${l}`} onClick={() => setActiveLang(l)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold font-mono transition-colors ${activeLang === l ? "bg-[#8B5CF6] text-white" : "text-[#94A3B8] hover:text-white"}`}>
              {l}{form.title[l === "EN" ? "en" : "it"] ? " ✓" : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 p-4">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm font-semibold text-white">Suggerisci argomenti</span>
                <span className="text-[11px] text-[#94A3B8]">— idee data-driven con Volume e Punto d'oro, prima del titolo</span>              </div>
              <button type="button" data-testid="suggest-topics-button" onClick={suggestTopics} disabled={topicsLoading}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#10B981] text-[#0B0D12] text-sm font-semibold hover:bg-[#34d399] transition-colors disabled:opacity-60">
                {topicsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{topicsLoading ? "Analisi…" : "Suggerisci argomenti"}
              </button>
            </div>
            <input
              data-testid="topic-seed-input"
              className="w-full rounded-lg bg-[#0B0D12] border border-[#10B981]/25 px-3 py-2 text-sm text-[#e2e8f0] placeholder:text-[#64748B] focus:outline-none focus:border-[#10B981]"
              value={topicSeed}
              onChange={(e) => setTopicSeed(e.target.value)}
              placeholder="Opzionale: mercato, nicchia, gap o idea di partenza (es. 'cuffie per home studio economiche')"
            />
            <label data-testid="topic-bilingual-toggle" className="mt-2 inline-flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" className="accent-[#10B981] w-3.5 h-3.5" checked={topicBilingual} onChange={(e) => setTopicBilingual(e.target.checked)} />
              <span className="text-[11px] font-semibold text-[#cbd5e1]">Argomenti IT + EN (bilingue) <span className="text-[#64748B] font-normal">— volumi e Punto d'oro per entrambi i mercati (2 chiamate)</span></span>
            </label>
            {topicsOpen && (
              <div data-testid="topic-ideas" className="mt-3 space-y-2">
                {!!topicIdeas.length && (
                  <p className="text-[11px] text-[#94A3B8] bg-[#0B0D12] border border-[#2D3748] rounded-lg px-3 py-2">
                    Per ogni idea scegli: <span className="text-[#34d399] font-semibold">Usa questo</span> = scrivi subito un <b>articolo singolo</b> · <span className="text-[#a78bfa] font-semibold">Piano</span> = salvala nel <b>piano editoriale</b> (con data e stato).
                  </p>
                )}
                {topicsLoading && !topicIdeas.length && (
                  <p className="text-[11px] text-[#94A3B8] flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Genero idee e recupero i volumi reali…</p>
                )}
                {topicIdeas.map((idea, i) => (
                  <div key={i} data-testid={`topic-idea-${i}`}
                    className="rounded-lg border border-[#10B981]/20 bg-[#0B0D12] p-3 hover:border-[#10B981] transition-colors">
                    {idea.bilingual ? (
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <span className="text-[9px] font-mono font-bold text-[#8B5CF6]">IT</span>
                            <p className="text-sm font-semibold text-[#e2e8f0]">{idea.title_it}</p>
                            <p className="text-[11px] text-[#67e8f9] mt-0.5"><Tag className="w-3 h-3 inline mr-1" />{idea.keyword_it}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span data-testid={`topic-gold-it-${i}`} className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#34d399]" title="Punto d'oro IT">★ {idea.gold_it ?? 0}</span>
                            <span data-testid={`topic-volume-it-${i}`} className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${(idea.volume_it || 0) > 0 ? "bg-[#06B6D4]/20 text-[#a5f3fc]" : "bg-[#334155]/40 text-[#94A3B8]"}`}>{idea.volume_it != null ? `${idea.volume_it}/mese` : "stima AI"}</span>
                          </div>
                        </div>
                        <div className="flex items-start justify-between gap-3 pt-2 border-t border-[#1e2533]">
                          <div className="min-w-0">
                            <span className="text-[9px] font-mono font-bold text-[#06B6D4]">EN</span>
                            <p className="text-sm font-semibold text-[#e2e8f0]">{idea.title_en}</p>
                            <p className="text-[11px] text-[#67e8f9] mt-0.5"><Tag className="w-3 h-3 inline mr-1" />{idea.keyword_en}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span data-testid={`topic-gold-en-${i}`} className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#34d399]" title="Punto d'oro EN">★ {idea.gold_en ?? 0}</span>
                            <span data-testid={`topic-volume-en-${i}`} className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${(idea.volume_en || 0) > 0 ? "bg-[#06B6D4]/20 text-[#a5f3fc]" : "bg-[#334155]/40 text-[#94A3B8]"}`}>{idea.volume_en != null ? `${idea.volume_en}/mo` : "stima AI"}</span>
                          </div>
                        </div>
                        {idea.angle ? <p className="text-[11px] text-[#94A3B8]">{idea.angle}</p> : null}
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#e2e8f0]">{idea.title}</p>
                          <p className="text-[11px] text-[#67e8f9] mt-0.5"><Tag className="w-3 h-3 inline mr-1" />{idea.keyword}</p>
                          {idea.angle ? <p className="text-[11px] text-[#94A3B8] mt-1">{idea.angle}</p> : null}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span data-testid={`topic-gold-${i}`} className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#34d399]" title="ABSIO Punto d'oro">★ {idea.gold ?? 0}</span>
                          <span data-testid={`topic-volume-${i}`} className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${(idea.search_volume || 0) > 0 ? "bg-[#06B6D4]/20 text-[#a5f3fc]" : "bg-[#334155]/40 text-[#94A3B8]"}`} title="Volume di ricerca mensile">{idea.search_volume != null ? `${idea.search_volume}/mese` : "stima AI"}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#1e2533]">
                      <button type="button" data-testid={`topic-use-${i}`} onClick={() => applyTopic(idea)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#10B981] text-[#0B0D12] text-xs font-semibold hover:bg-[#34d399] transition-colors">
                        <Check className="w-3.5 h-3.5" /> Usa questo
                      </button>
                      <button type="button" data-testid={`topic-add-plan-${i}`} onClick={() => addToPlan(idea)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#8B5CF6]/40 text-[#a78bfa] text-xs font-semibold hover:bg-[#8B5CF6]/10 transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Piano
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] flex items-center gap-2">
                Title ({activeLang})
                {translating && (
                  <span data-testid="title-translating" className="inline-flex items-center gap-1 text-[10px] font-medium text-[#06B6D4] normal-case tracking-normal">
                    <Loader2 className="w-3 h-3 animate-spin" /> traduzione EN…
                  </span>
                )}
              </label>
              <div className="flex items-center gap-3">
                <UndoBtn field="title" />
                <RedoBtn field="title" />
                <button type="button" data-testid="inject-keyword-button" onClick={() => setKwPickerOpen((o) => !o)} disabled={injectingKw}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#06B6D4] hover:text-[#67e8f9] transition-colors disabled:opacity-60">
                  {injectingKw ? <Loader2 className="w-3 h-3 animate-spin" /> : <Tag className="w-3 h-3" />}{injectingKw ? "…" : "Inserisci keyword"}
                </button>
                <button type="button" data-testid="suggest-title-button" onClick={suggestTitle} disabled={suggestingTitle}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors disabled:opacity-60">
                  {suggestingTitle ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{suggestingTitle ? "…" : "Titolo SEO AI"}
                </button>
              </div>
            </div>
            {kwPickerOpen && (
              <div data-testid="kw-picker" className="mb-2 rounded-xl border border-[#06B6D4]/30 bg-[#06B6D4]/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold text-[#67e8f9] flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Scegli la keyword da inserire nel titolo</p>
                  <button type="button" onClick={() => setKwPickerOpen(false)} className="text-[#94A3B8] hover:text-white"><X className="w-3.5 h-3.5" /></button>
                </div>
                {kwCandidates.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {kwCandidates.map((k, i) => (
                      <button type="button" key={k} data-testid={`kw-pick-${i}`} onClick={() => injectKeyword(k)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-[#06B6D4]/40 bg-[#0B0D12] text-[#e2e8f0] hover:bg-[#06B6D4]/15 hover:border-[#06B6D4] transition-colors">
                        <Plus className="w-3 h-3 text-[#06B6D4]" /> {k}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-[#94A3B8]">Nessuna keyword ancora: aggiungine una nel campo keyword o usa "Suggerisci AI".</p>
                )}
                <button type="button" data-testid="kw-pick-manual" onClick={injectKeywordManual}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#94A3B8] hover:text-white transition-colors">
                  <Pencil className="w-3 h-3" /> Scrivi un'altra keyword…
                </button>
              </div>
            )}
            <input
              data-testid="input-title"
              className={field}
              value={curTitle}
              onChange={(e) => setLang("title", e.target.value)}
              onBlur={(e) => { if (activeLang === "IT" && e.target.value.trim() && !form.title.en) autoTranslateTitleEN(e.target.value); }}
              placeholder="Article title"
            />
            {titleListOpen && titleSuggestions.length > 0 && (
              <div data-testid="title-suggestions" className="flex flex-col gap-2 mt-2">
                {titleSuggestions.map((t, i) => (
                  <button type="button" key={t} data-testid={`title-suggestion-${i}`} onClick={() => applyTitleSuggestion(t)}
                    className="text-left px-3 py-2 rounded-lg text-sm border border-[#8B5CF6]/25 bg-[#8B5CF6]/5 text-[#e2e8f0] hover:bg-[#8B5CF6]/15 hover:border-[#8B5CF6]/50 transition-colors">{t}</button>
                ))}
              </div>
            )}
            {!titleListOpen && titleSuggestions.length > 0 && (
              <button type="button" data-testid="show-title-suggestions" onClick={() => setTitleListOpen(true)}
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors">
                <Sparkles className="w-3 h-3" /> Mostra proposte titolo ({titleSuggestions.length})
              </button>
            )}
          </div>

          <div className="rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#8B5CF6]" />
              <span className="text-sm font-semibold text-white">AI generation — {activeLang}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_260px] gap-3 mb-3">
              <div>
                <label className={labelCls}>Keyword(s)</label>
                <input data-testid="input-keyword" className={field} value={curKeyword} onChange={(e) => setLang("keyword", e.target.value)} placeholder={`Keyword ${activeLang} (es: keyword1, keyword2)`} />
              </div>
              <div>
                <label className={labelCls}>Mode</label>
                <select data-testid="select-mode" className={field} value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="auto">AUTO — generate & fill</option>
                  <option value="assisted">ASSISTED — draft to edit</option>
                </select>
              </div>
            </div>
            <div data-testid="keyword-toolbar" className="flex items-center gap-x-4 gap-y-2 flex-wrap mb-3 rounded-xl border border-[#2D3748] bg-[#0B0D12]/60 px-3 py-2">
              <UndoBtn field="keyword" />
              <RedoBtn field="keyword" />
              <button type="button" data-testid="translate-keywords" onClick={translateKeywords} disabled={translatingKw}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors disabled:opacity-60">
                {translatingKw ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}{translatingKw ? "…" : (activeLang === "IT" ? "Traduci → EN" : "Traduci → IT")}
              </button>
              <button type="button" data-testid="real-questions-button" onClick={fetchQuestions} disabled={paaLoading || realKw || suggesting}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F59E0B] hover:text-[#fbbf24] transition-colors disabled:opacity-60">
                {paaLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <HelpCircle className="w-3 h-3" />}{paaLoading ? "…" : "Domande"}
              </button>
              <button type="button" data-testid="real-keywords-button" onClick={realKeywords} disabled={realKw || suggesting}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#10B981] hover:text-[#34d399] transition-colors disabled:opacity-60">
                {realKw ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}{realKw ? "…" : "Keyword reali"}
              </button>
              <button type="button" data-testid="suggest-keywords-button" onClick={suggestKeywords} disabled={suggesting || realKw || longtailKw}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#06B6D4] hover:text-[#22d3ee] transition-colors disabled:opacity-60">
                {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{suggesting ? "…" : "Suggerisci AI"}
              </button>
              <button type="button" data-testid="longtail-keywords-button" onClick={longtailKeywords} disabled={longtailKw || realKw || suggesting}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors disabled:opacity-60">
                {longtailKw ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{longtailKw ? "…" : "Long-tail AI"}
              </button>
              <span className="inline-flex items-center gap-1 rounded-full border border-[#10B981]/30 bg-[#10B981]/5 pl-2 pr-0.5 py-0.5">
                <select data-testid="seo-market-select" value={seoMarket} onChange={(e) => setSeoMarket(e.target.value)}
                  className="bg-transparent text-[11px] font-semibold text-[#34d399] outline-none cursor-pointer">
                  <option value="it" className="bg-[#0B0D12]">🇮🇹 Italia</option>
                  <option value="us" className="bg-[#0B0D12]">🇺🇸 USA</option>
                  <option value="uk" className="bg-[#0B0D12]">🇬🇧 UK</option>
                  <option value="es" className="bg-[#0B0D12]">🇪🇸 Spagna</option>
                  <option value="fr" className="bg-[#0B0D12]">🇫🇷 Francia</option>
                  <option value="de" className="bg-[#0B0D12]">🇩🇪 Germania</option>
                </select>
                <button type="button" data-testid="real-volume-button" onClick={fetchRealVolumes} disabled={volLoading}
                  title="Volumi di ricerca reali"
                  className="inline-flex items-center gap-1 rounded-full bg-[#10B981] px-2 py-1 text-[11px] font-bold text-[#0B0D12] hover:bg-[#34d399] transition-colors disabled:opacity-60">
                  {volLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}{volLoading ? "…" : "Volumi reali"}
                </button>
              </span>
              <button type="button" data-testid="seo-report-button" onClick={downloadSeoReport} disabled={reportLoading}
                title="Scarica report PDF white-label per il cliente"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F59E0B] hover:text-[#fbbf24] transition-colors disabled:opacity-60">
                {reportLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3 rotate-180" />}{reportLoading ? "…" : "Report PDF"}
              </button>
            </div>
            <div className="mb-3">
              <label className={labelCls}>Testo / idea da rielaborare con l'AI (opzionale)</label>
              <textarea
                data-testid="input-source-text"
                className={`${field} text-xs leading-relaxed`}
                rows={5}
                value={sourceText}
                onChange={(e) => setSourceText(e.target.value)}
                placeholder="Incolla qui un testo grezzo, appunti o una semplice idea: l'AI lo sistemerà e lo trasformerà in un articolo completo. Lascia vuoto per generare dal solo titolo/keyword."
              />
            </div>
            {curKeywordSuggestions.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <p data-testid="kw-legend" className="text-[11px] text-[#64748B]">Clicca per aggiungere · <b className="text-[#34d399]">lente verde</b> = ricerche reali Google/mese · <b className="text-[#94A3B8]">n.d.</b> = nessun dato Google · numero <b className="text-[#a5f3fc]">cyan</b> = popolarità (stima AI) · <b className="text-[#34d399]">KD</b> = difficoltà reale · facile/medio/difficile = stima AI</p>
                  <button type="button" data-testid="kw-use-recommended" onClick={useRecommendedAsMain} disabled={!recommendedKws.size}
                    title={recommendedKws.size ? "Imposta la miglior keyword con dati reali come keyword principale" : "Premi prima \"Volumi reali\""}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold shrink-0 bg-[#F59E0B]/20 text-[#fbbf24] border border-[#F59E0B]/40 hover:bg-[#F59E0B]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    <Star className="w-3 h-3 fill-current" />Usa la consigliata come principale
                  </button>
                  <div data-testid="kw-goal-toggle" className="inline-flex items-center rounded-full border border-[#2D3748] bg-[#0B0D12] p-0.5 text-[10px] font-semibold shrink-0">
                    <span className="px-2 text-[#64748B]">Obiettivo:</span>
                    {Object.entries(KW_GOALS).map(([k, g]) => (
                      <button key={k} type="button" data-testid={`kw-goal-${k}`} title={g.hint} onClick={() => setKwGoal(k)}
                        className={`px-2.5 py-1 rounded-full transition-colors ${kwGoal === k ? "bg-[#F59E0B] text-[#0B0D12]" : "text-[#94A3B8] hover:text-white"}`}>{g.label}</button>
                    ))}
                  </div>
                  <div data-testid="kw-sort-toggle" className="inline-flex items-center rounded-full border border-[#2D3748] bg-[#0B0D12] p-0.5 text-[10px] font-semibold shrink-0">
                    <button type="button" data-testid="kw-sort-pop" onClick={() => setKwSort("pop")}
                      className={`px-2.5 py-1 rounded-full transition-colors ${kwSort === "pop" ? "bg-[#06B6D4] text-[#0B0D12]" : "text-[#94A3B8] hover:text-white"}`}>Popolarità</button>
                    <button type="button" data-testid="kw-sort-gold" onClick={() => setKwSort("gold")}
                      className={`px-2.5 py-1 rounded-full transition-colors ${kwSort === "gold" ? "bg-[#10B981] text-[#0B0D12]" : "text-[#94A3B8] hover:text-white"}`}>★ ABSIO Punto d'oro</button>
                  </div>
                </div>
                <div data-testid="keyword-suggestions" className="flex flex-wrap gap-2">
                  {sortedSuggestions.map((kw, i) => {
                    const active = selectedKeywords.includes(kw.keyword);
                    const rd = realDiff(kw);
                    const diff = rd ? rd.label : (kw.difficulty || "medio");
                    const diffStyle = diff === "facile"
                      ? { dot: "#10B981", label: "facile", cls: active ? "bg-[#0B0D12]/20 text-[#0B0D12]" : "bg-[#10B981]/15 text-[#34d399]" }
                      : diff === "difficile"
                      ? { dot: "#EF4444", label: "difficile", cls: active ? "bg-[#0B0D12]/20 text-[#0B0D12]" : "bg-[#EF4444]/15 text-[#fca5a5]" }
                      : { dot: "#F59E0B", label: "medio", cls: active ? "bg-[#0B0D12]/20 text-[#0B0D12]" : "bg-[#F59E0B]/15 text-[#fbbf24]" };
                    return (
                      <button type="button" key={kw.keyword} data-testid={`keyword-chip-${i}`} onClick={() => toggleKeyword(kw.keyword)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-[#06B6D4] text-[#0B0D12] border-[#06B6D4]" : "bg-[#06B6D4]/5 text-[#67e8f9] border-[#06B6D4]/30 hover:bg-[#06B6D4]/15"}`}>
                        {recommendedKws.has(kw.keyword) && (
                          <span data-testid={`keyword-recommended-${i}`} title="Consigliata: ricerche reali Google + miglior Punto d'oro"
                            className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-[#0B0D12]/20 text-[#0B0D12]" : "bg-[#F59E0B]/20 text-[#fbbf24]"}`}>
                            <Star className="w-2.5 h-2.5 fill-current" />Consigliata
                          </span>
                        )}
                        {selectedKeywords[0] === kw.keyword && (
                          <span data-testid={`keyword-main-${i}`} className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#0B0D12]/25 text-[#0B0D12]">Principale</span>
                        )}
                        <span>{kw.keyword}</span>
                        {realVolumes[kw.keyword.toLowerCase()] && (() => {
                          const rv = realVolumes[kw.keyword.toLowerCase()];
                          const vol = rv.search_volume || 0;
                          const hasVol = vol > 0;
                          const cls = active
                            ? "bg-[#0B0D12]/20 text-[#0B0D12]"
                            : hasVol ? "bg-[#10B981]/20 text-[#34d399]" : "bg-[#64748B]/20 text-[#94A3B8]";
                          return (
                            <span data-testid={`keyword-volume-${i}`}
                              title={hasVol
                                ? `Ricerche reali Google: ${vol}/mese · CPC ${rv.cpc != null ? `€${rv.cpc.toFixed(2)}` : "n.d. (nessun inserzionista)"} · KD ${rv.competition_index ?? "n.d."}/100`
                                : "n.d. = Google non registra un volume per questa frase (troppo specifica). Usala come keyword secondaria."}
                              className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full ${cls}`}>
                              <Search className="w-2.5 h-2.5" />{hasVol ? `${vol}/mo` : "n.d."}
                            </span>
                          );
                        })()}
                        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-[#0B0D12]/20 text-[#0B0D12]" : "bg-[#06B6D4]/20 text-[#a5f3fc]"}`}>{kw.popularity}</span>
                        <span data-testid={`keyword-difficulty-${i}`} title={rd ? `Difficoltà reale (DataForSEO): ${rd.label} · KD ${rd.ci}/100` : `Difficoltà (stima AI): ${diffStyle.label}`}
                          className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${diffStyle.cls}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: diffStyle.dot }} />{rd ? `KD ${rd.ci}` : diffStyle.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {curRelatedTerms.length > 0 && (
              <div className="mb-3">
                <p className="text-[11px] text-[#64748B] mb-2">Parole correlate (inserite nel corpo alla generazione · clicca per copiare)</p>
                <div data-testid="related-terms" className="flex flex-wrap gap-2">
                  {curRelatedTerms.map((rt, i) => (
                    <button type="button" key={rt} data-testid={`related-term-${i}`} onClick={() => { try { navigator.clipboard?.writeText(rt)?.catch(() => {}); } catch (e) {} toast.success("Copiato"); }}
                      className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#2D3748] bg-[#121620] text-[#94A3B8] hover:text-white hover:border-[#8B5CF6]/40 transition-colors">{rt}</button>
                  ))}
                </div>
              </div>
            )}
            {curPaaQuestions.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <HelpCircle className="w-3.5 h-3.5 text-[#F59E0B]" />
                  <p className="text-[11px] font-semibold text-[#fbbf24]">Domande della gente (reali) · genera o scrivi la risposta, poi inseriscila. "＋" inserisce solo la domanda (H2)</p>
                </div>
                <div data-testid="paa-block" className="flex flex-col gap-2">
                  {curPaaQuestions.map((q, i) => {
                    const open = !!faqOpen[lk]?.[i];
                    const loadingAns = faqAnswering === `${lk}-${i}`;
                    return (
                      <div key={q} className="rounded-lg border border-[#F59E0B]/25 bg-[#F59E0B]/5">
                        <div className="flex items-stretch gap-2 p-1.5">
                          <button type="button" data-testid={`paa-question-${i}`} onClick={() => toggleFaq(i)}
                            className="flex-1 text-left px-2.5 py-1.5 rounded-md text-sm text-[#e2e8f0] hover:bg-[#F59E0B]/10 transition-colors flex items-start gap-2">
                            <HelpCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#F59E0B]" /> <span>{q}</span>
                            {realVolumes[(q || "").toLowerCase()] && (() => {
                              const rv = realVolumes[(q || "").toLowerCase()];
                              const vol = rv.search_volume || 0;
                              return (
                                <span data-testid={`paa-volume-${i}`} title={vol > 0 ? `Ricerche reali Google: ${vol}/mese` : "n.d. = nessun volume Google registrato"}
                                  className={`ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full ${vol > 0 ? "bg-[#10B981]/20 text-[#34d399]" : "bg-[#64748B]/20 text-[#94A3B8]"}`}>
                                  <Search className="w-2.5 h-2.5" />{vol > 0 ? `${vol}/mo` : "n.d."}
                                </span>
                              );
                            })()}
                          </button>
                          <button type="button" data-testid={`paa-insert-h2-${i}`} title="Inserisci solo la domanda (H2)"
                            onClick={() => { insertAtCursor(`\n<h2>${q}</h2>\n<p></p>\n`); toast.success("Domanda inserita nel corpo (H2)"); }}
                            className="shrink-0 inline-flex items-center px-2 rounded-md text-[#F59E0B] hover:bg-[#F59E0B]/15 transition-colors">
                            <Plus className="w-4 h-4" />
                          </button>
                          <button type="button" data-testid={`paa-answer-${i}`} title="Genera risposta AI (modificabile)"
                            onClick={() => generateAnswer(q, i)} disabled={loadingAns}
                            className="shrink-0 inline-flex items-center gap-1 px-2.5 rounded-md text-xs font-semibold border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#a78bfa] hover:bg-[#8B5CF6]/20 transition-colors disabled:opacity-50">
                            {loadingAns ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}<span className="hidden sm:inline">risposta AI</span>
                          </button>
                        </div>
                        {open && (
                          <div className="px-2.5 pb-2.5 space-y-2">
                            <textarea data-testid={`paa-answer-text-${i}`} rows={4}
                              value={faqAnswers[lk]?.[i] || ""} onChange={(e) => setFaqText(i, e.target.value)}
                              placeholder="Scrivi o incolla la risposta, oppure premi 'risposta AI' per generarla"
                              className="w-full bg-[#0B0D12] border border-[#2D3748] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#8B5CF6] resize-y" />
                            <div className="flex flex-wrap items-center gap-2">
                              <button type="button" data-testid={`paa-insert-qa-${i}`} onClick={() => insertQA(q, i)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#8B5CF6] text-white text-xs font-semibold hover:bg-[#7C3AED] transition-colors">
                                <Plus className="w-3.5 h-3.5" /> Inserisci Q+A nel corpo
                              </button>
                              <button type="button" data-testid={`paa-regen-${i}`} onClick={() => generateAnswer(q, i)} disabled={loadingAns}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#8B5CF6]/40 text-[#a78bfa] text-xs font-semibold hover:bg-[#8B5CF6]/10 transition-colors disabled:opacity-50">
                                {loadingAns ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Rigenera
                              </button>
                              <button type="button" onClick={() => toggleFaq(i)} className="text-xs text-[#94A3B8] hover:text-white px-2">Chiudi</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button data-testid="generate-button" onClick={generate} disabled={generating}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#8B5CF6] text-white text-sm font-semibold hover:bg-[#7C3AED] transition-colors disabled:opacity-60">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{generating ? "Generating…" : `Generate ${activeLang}`}
              </button>
              <button data-testid="generate-both-button" onClick={generateBoth} disabled={generating}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#8B5CF6]/40 text-[#a78bfa] text-sm font-semibold hover:bg-[#8B5CF6]/10 transition-colors disabled:opacity-60">
                <Sparkles className="w-4 h-4" /> Genera IT + EN
              </button>
              <button data-testid="analyze-serp-button" onClick={analyzeSerp} disabled={serpLoading || generating}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#06B6D4]/40 text-[#67e8f9] text-sm font-semibold hover:bg-[#06B6D4]/10 transition-colors disabled:opacity-60">
                {serpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />}{serpLoading ? "Analisi…" : "Analizza SERP"}
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Body — {activeLang} (HTML)</label>
              <div className="flex items-center gap-3 flex-wrap">
                <UndoBtn field="body" />
                <RedoBtn field="body" />
                <button type="button" data-testid="translate-body" onClick={translateBody} disabled={translatingBody}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#06B6D4] hover:text-[#67e8f9] transition-colors disabled:opacity-60">
                  {translatingBody ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
                  {translatingBody ? "Traduzione…" : (activeLang === "IT" ? "Traduci → EN" : "Traduci → IT")}
                </button>
                <button type="button" data-testid="inject-keyword-body-button" onClick={() => setKwBodyPickerOpen((o) => !o)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#06B6D4] hover:text-[#67e8f9] transition-colors">
                  <Tag className="w-3 h-3" /> Inserisci keyword
                </button>
                {(curBody || "").includes("`") && (
                  <button type="button" data-testid="clean-backticks-button" onClick={cleanBackticks}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#F59E0B] hover:text-[#fbbf24] transition-colors">
                    <X className="w-3 h-3" /> Pulisci backtick
                  </button>
                )}
                <button type="button" data-testid="insert-external-link" onMouseDown={(e) => e.preventDefault()} onClick={insertExternalLink} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors">
                  <ExternalLink className="w-3 h-3" /> Link esterno
                </button>
                <button type="button" data-testid="insert-internal-link" onMouseDown={(e) => e.preventDefault()} onClick={insertInternalLink} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors">
                  <Link2 className="w-3 h-3" /> Link interno
                </button>
                <button type="button" data-testid="insert-video" onClick={insertVideo} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors">
                  <Video className="w-3 h-3" /> Video
                </button>
                <label data-testid="insert-body-image-label" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] cursor-pointer transition-colors">
                  {insertingImg ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
                  {insertingImg ? "Caricamento…" : "Immagine"}
                  <input data-testid="insert-body-image" type="file" accept="image/*" className="hidden" onChange={insertBodyImage} />
                </label>
              </div>
            </div>
            {kwBodyPickerOpen && (
              <div data-testid="kw-body-picker" className="mb-2 rounded-xl border border-[#06B6D4]/30 bg-[#06B6D4]/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold text-[#67e8f9] flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Scegli la keyword da inserire nel corpo (al cursore, in grassetto)</p>
                  <button type="button" onClick={() => setKwBodyPickerOpen(false)} className="text-[#94A3B8] hover:text-white"><X className="w-3.5 h-3.5" /></button>
                </div>
                {kwCandidates.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {kwCandidates.map((k, i) => (
                      <button type="button" key={k} data-testid={`kw-body-pick-${i}`} onClick={() => insertKeywordBody(k)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-[#06B6D4]/40 bg-[#0B0D12] text-[#e2e8f0] hover:bg-[#06B6D4]/15 hover:border-[#06B6D4] transition-colors">
                        <Plus className="w-3 h-3 text-[#06B6D4]" /> {k}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-[#94A3B8]">Nessuna keyword ancora: aggiungine una nel campo keyword o usa "Suggerisci AI".</p>
                )}
                <button type="button" data-testid="kw-body-pick-manual" onClick={insertKeywordBodyManual}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#94A3B8] hover:text-white transition-colors">
                  <Pencil className="w-3 h-3" /> Scrivi un'altra keyword…
                </button>
              </div>
            )}
            {(serpCompetitors[lk] || []).length > 0 && (
              <div data-testid="serp-competitors" className="mb-2 rounded-lg border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 p-3">
                <p className="text-[11px] font-semibold text-[#a78bfa] mb-1.5 flex items-center gap-1.5">
                  <Search className="w-3 h-3" /> Top {(serpCompetitors[lk] || []).length} concorrenti SERP analizzati (l'AI scrive per batterli)
                </p>
                <ol className="space-y-1 list-decimal list-inside">
                  {(serpCompetitors[lk] || []).map((c, i) => (
                    <li key={i} data-testid={`serp-competitor-${i}`} className="text-[11px] text-[#cbd5e1]">
                      <a href={c.url} target="_blank" rel="noopener noreferrer" className="font-medium text-[#67e8f9] hover:underline">{c.title || c.url}</a>
                      {c.description ? <span className="text-[#94A3B8]"> — {c.description.slice(0, 90)}…</span> : null}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {(serpGaps[lk] || []).length > 0 && (
              <div data-testid="content-gaps" className="mb-2 rounded-lg border border-[#06B6D4]/30 bg-[#06B6D4]/5 p-3">
                <p className="text-[11px] font-semibold text-[#67e8f9] mb-1.5 flex items-center gap-1.5">
                  <Lightbulb className="w-3 h-3" /> Gap di contenuto — argomenti che i concorrenti NON trattano (l'AI li userà per differenziare)
                </p>
                <ul className="space-y-1">
                  {(serpGaps[lk] || []).map((g, i) => (
                    <li key={i} data-testid={`content-gap-${i}`} className="text-[11px] text-[#cbd5e1] flex items-start gap-1.5">
                      <Plus className="w-3 h-3 mt-0.5 shrink-0 text-[#06B6D4]" /> <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <textarea ref={bodyRef} data-testid="input-body" className={`${field} font-mono text-xs leading-relaxed`} rows={16} value={curBody}
              onKeyDown={(e) => {
                const mod = e.ctrlKey || e.metaKey;
                if (mod && (e.key === "z" || e.key === "Z")) {
                  e.preventDefault();
                  if (e.shiftKey) doRedo("body"); else doUndo("body");
                } else if (mod && (e.key === "y" || e.key === "Y")) {
                  e.preventDefault();
                  doRedo("body");
                }
              }}
              onChange={(e) => {
                const now = Date.now();
                if (now - typingRef.current.time > 700 || typingRef.current.lang !== lk) {
                  snapshot("body", lk, curBody);
                }
                typingRef.current = { time: now, lang: lk };
                setLang("body", e.target.value);
              }}
              onSelect={(e) => { bodySelRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
              placeholder="Article body. Use AI generation or write HTML here." />
            <p className="text-[11px] text-[#64748B] mt-1">Gli elementi vengono inseriti nel punto del cursore. Immagine e video occupano tutta la larghezza.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">SEO meta description ({activeLang})</label>
              <div className="flex items-center gap-3">
                <span className={`text-[11px] font-mono ${curMeta.length > 160 ? "text-red-400" : curMeta.length >= 50 ? "text-emerald-400" : "text-[#64748B]"}`}>{curMeta.length}/160</span>
                <UndoBtn field="meta_description" />
                <RedoBtn field="meta_description" />
                <button type="button" data-testid="translate-meta" onClick={translateMeta} disabled={translatingMeta}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors disabled:opacity-60">
                  {translatingMeta ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}{translatingMeta ? "…" : (activeLang === "IT" ? "Traduci → EN" : "Traduci → IT")}
                </button>
                <button type="button" data-testid="inject-keyword-meta-button" onClick={() => setKwMetaPickerOpen((o) => !o)} disabled={injectingKwMeta}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#06B6D4] hover:text-[#67e8f9] transition-colors disabled:opacity-60">
                  {injectingKwMeta ? <Loader2 className="w-3 h-3 animate-spin" /> : <Tag className="w-3 h-3" />}{injectingKwMeta ? "…" : "Inserisci keyword"}
                </button>
                <button type="button" data-testid="suggest-meta-button" onClick={suggestMeta} disabled={suggestingMeta}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors disabled:opacity-60">
                  {suggestingMeta ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{suggestingMeta ? "…" : "Meta AI"}
                </button>
              </div>
            </div>
            {kwMetaPickerOpen && (
              <div data-testid="kw-meta-picker" className="mb-2 rounded-xl border border-[#06B6D4]/30 bg-[#06B6D4]/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold text-[#67e8f9] flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Scegli la keyword da inserire nella meta</p>
                  <button type="button" onClick={() => setKwMetaPickerOpen(false)} className="text-[#94A3B8] hover:text-white"><X className="w-3.5 h-3.5" /></button>
                </div>
                {kwCandidates.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {kwCandidates.map((k, i) => (
                      <button type="button" key={k} data-testid={`kw-meta-pick-${i}`} onClick={() => injectKeywordMeta(k)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-[#06B6D4]/40 bg-[#0B0D12] text-[#e2e8f0] hover:bg-[#06B6D4]/15 hover:border-[#06B6D4] transition-colors">
                        <Plus className="w-3 h-3 text-[#06B6D4]" /> {k}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-[#94A3B8]">Nessuna keyword ancora: aggiungine una nel campo keyword o usa "Suggerisci AI".</p>
                )}
                <button type="button" data-testid="kw-meta-pick-manual" onClick={injectKeywordMetaManual}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#94A3B8] hover:text-white transition-colors">
                  <Pencil className="w-3 h-3" /> Scrivi un'altra keyword…
                </button>
              </div>
            )}
            <textarea data-testid="input-meta" className={field} rows={2} value={curMeta} onChange={(e) => setLang("meta_description", e.target.value)} placeholder="Under 160 characters" />
          </div>

          <div data-testid="seo-panel" className="rounded-xl border border-[#2D3748] bg-[#121620] p-4 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Punteggio SEO ({activeLang})</span>
                <span data-testid="seo-score" className="text-sm font-bold font-mono" style={{ color: seoColor }}>{seo.score}/100</span>
              </div>
              <div className="h-2 w-full rounded-full bg-[#0B0D12] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${seo.score}%`, backgroundColor: seoColor }} />
              </div>
              <button type="button" data-testid="ai-analyze-optimize-button" onClick={analyzeOptimize} disabled={auditing || !(curBody || "").trim()}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4] text-white text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed">
                {auditing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {auditing ? "Analisi in corso…" : "Analisi AI e Ottimizza"}
              </button>
              <button type="button" data-testid="social-adapt-button" onClick={() => { setSocialOpen(true); setSocialResult(null); setSocialPlatform(null); }} disabled={!(curBody || "").trim()}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-[#2D3748] bg-[#121620] text-[#CBD5E1] text-xs font-bold hover:border-[#8B5CF6] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <Share2 className="w-4 h-4" /> Adatta per Social
              </button>
              <ul className="mt-3 space-y-1.5">
                {seo.checks.map((c) => (
                  <li key={c.label} className="text-xs">
                    <div className="flex items-center gap-2">
                      {c.ok ? <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <X className="w-3.5 h-3.5 text-[#64748B] shrink-0" />}
                      <span className={c.ok ? "text-[#CBD5E1]" : "text-[#64748B]"}>{c.label}</span>
                      {c.ok && justFixed[c.fix] && (
                        <span data-testid="seo-fixed-badge" className="inline-flex items-center gap-0.5 text-emerald-400 font-semibold" style={{ fontSize: "0.65rem" }}><Check className="w-3 h-3" /> Corretto</span>
                      )}
                    </div>
                    {!c.ok && c.hint && (
                      <p data-testid="seo-hint" className="ml-5 mt-0.5 text-[#F59E0B]" style={{ fontSize: "0.7rem" }}>{c.hint}</p>
                    )}
                    {!c.ok && c.fix && seoFixers[c.fix] && (
                      <button type="button" data-testid={`seo-fix-${c.fix}`} onClick={seoFixers[c.fix]}
                        className="ml-5 mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#a78bfa] hover:bg-[#8B5CF6]/20 transition-colors font-semibold" style={{ fontSize: "0.7rem" }}>
                        <Sparkles className="w-3 h-3" /> Correggi automaticamente
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            {density && (() => {
              const cfg = {
                none: { color: "#EF4444", bg: "bg-red-500/10 border-red-500/30", text: "text-red-400", label: "Assente", hint: "La keyword non compare nel corpo: inseriscila almeno 1–2 volte." },
                low: { color: "#F59E0B", bg: "bg-[#F59E0B]/10 border-[#F59E0B]/30", text: "text-[#fbbf24]", label: "Troppo poca", hint: "Aggiungi qualche occorrenza in più (ideale 0,5%–2,5%)." },
                ok: { color: "#10B981", bg: "bg-emerald-500/10 border-emerald-500/30", text: "text-emerald-400", label: "Ottimale", hint: "Densità nella fascia consigliata (0,5%–2,5%)." },
                high: { color: "#EF4444", bg: "bg-red-500/10 border-red-500/30", text: "text-red-400", label: "Eccessiva", hint: "Troppa ripetizione (keyword stuffing): riducila sotto il 2,5%." },
              }[density.status];
              return (
                <div data-testid="keyword-density" className={`rounded-lg border p-3 ${cfg.bg}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">Densità keyword</span>
                    <span data-testid="keyword-density-status" className={`text-[11px] font-bold ${cfg.text}`}>{cfg.label}</span>
                  </div>
                  <p className="text-xs text-[#CBD5E1]">
                    «<span className="font-medium">{density.kw}</span>» ·{" "}
                    <span data-testid="keyword-density-count" className="font-mono font-bold" style={{ color: cfg.color }}>{density.count}</span> volte su {density.total} parole ·{" "}
                    <span className="font-mono font-bold" style={{ color: cfg.color }}>{density.density.toFixed(1)}%</span>
                  </p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">{cfg.hint}</p>
                  {(density.status === "low" || density.status === "none") && (
                    <button type="button" data-testid="distribute-keyword-button" onClick={distributeKeyword} disabled={distributing}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#8B5CF6] text-white text-[11px] font-semibold hover:bg-[#7C3AED] transition-colors disabled:opacity-60">
                      {distributing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {distributing ? "Distribuzione…" : "Distribuisci keyword nell'articolo"}
                    </button>
                  )}
                </div>
              );
            })()}
            {seoGap >= 20 && (
              <div data-testid="seo-gap-warning" className="rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3">
                <div className="flex items-start gap-2">
                  <HelpCircle className="w-4 h-4 text-[#fbbf24] shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[11px] font-semibold text-[#fbbf24] mb-1">Punteggi SEO sbilanciati ({seoGap} punti di differenza)</p>
                    <p className="text-[11px] text-[#CBD5E1] mb-2">
                      IT: <span className="font-mono font-bold">{seoIt.score}</span> · EN: <span className="font-mono font-bold">{seoEn.score}</span>. Rifinisci la scheda <span className="font-bold">{seoLower}</span> (punteggio più basso) per riavvicinarli.
                    </p>
                    {activeLang !== seoLower && (
                      <button type="button" data-testid="goto-lower-seo" onClick={() => setActiveLang(seoLower)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#fbbf24] hover:text-[#fde68a] transition-colors">
                        <ExternalLink className="w-3 h-3" />Vai alla scheda {seoLower}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Anteprima Google</span>
              <div data-testid="google-preview" className="mt-2 rounded-lg bg-white p-3 border border-[#2D3748]">
                <div className="text-[#202124] text-xs mb-0.5 font-medium">Spectraloom</div>
                <div className="text-[#4d5156] text-[11px] leading-none mb-1">{host} › {form.category} › {slug}</div>
                <div className="text-[#1a0dab] text-[18px] leading-tight truncate" style={{ fontFamily: "arial, sans-serif" }}>{curTitle || "Titolo dell'articolo"}</div>
                <div className="text-[#4d5156] text-[13px] leading-snug mt-1" style={{ fontFamily: "arial, sans-serif", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {curMeta || "La meta description apparirà qui."}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className={labelCls}>Profilo cliente</label>
            <select data-testid="select-client" className={field} value={form.tenant || "spectraloom"}
              onChange={(e) => {
                const tv = e.target.value;
                const cl = clients.find((c) => c.tenant === tv);
                setForm((f) => {
                  const next = { ...f, tenant: tv };
                  if (!isEdit && cl && Array.isArray(cl.default_keywords) && cl.default_keywords.length && !(f.keyword?.it || f.keyword?.en)) {
                    const kw = cl.default_keywords.join(", ");
                    next.keyword = { it: kw, en: kw };
                  }
                  return next;
                });
              }}>
              {clients.map((c) => <option key={c.tenant} value={c.tenant}>{c.name}{c.is_default ? " (predefinito)" : ""}</option>)}
            </select>
            {form.tenant && form.tenant !== "spectraloom" && (
              <p data-testid="tenant-private-note" className="mt-1.5 text-[11px] text-[#fbbf24]">Questo profilo NON viene pubblicato sul blog pubblico Spectraloom: resta visibile solo qui nel pannello.</p>
            )}
          </div>

          <div>
            <label className={labelCls}>Category</label>
            <select data-testid="select-category" className={field} value={form.category} onChange={(e) => set("category", e.target.value)}>
              {SECTIONS.map((s) => <option key={s.slug} value={s.slug}>{s.slug === "case_studies" ? caseStudiesLabel : s.en}</option>)}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">URL slug</label>
              <button type="button" data-testid="suggest-slug-button" onClick={suggestSlug} disabled={suggestingSlug}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors disabled:opacity-60">
                {suggestingSlug ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{suggestingSlug ? "…" : "Ottimizza AI"}
              </button>
            </div>
            <input
              data-testid="input-slug"
              className={`${field} font-mono text-xs`}
              value={form.slug || ""}
              onChange={(e) => set("slug", e.target.value)}
              onBlur={(e) => set("slug", slugify(e.target.value))}
              placeholder={slugify(curTitle) || "generato-dal-titolo"}
            />
            <p className="text-[11px] text-[#64748B] mt-1 break-all">
              URL pubblico: <span className="text-[#94A3B8]">spectraloom.app/blog/{slug}</span>
            </p>
            {slugChangedWhilePublished && (
              <p data-testid="slug-warning" className="text-[11px] text-amber-400 mt-1.5 flex items-start gap-1.5">
                <X className="w-3.5 h-3.5 shrink-0 mt-px" />
                Attenzione: questo articolo è già pubblicato. Cambiando lo slug cambierà l'URL pubblico e i link esistenti smetteranno di funzionare.
              </p>
            )}
          </div>

          <div>
            <label className={labelCls}>Cover image</label>
            <p className="text-[11px] text-[#64748B] -mt-1 mb-2">
              Consigliato: <span className="text-[#94A3B8] font-medium">1200×630px</span>, orizzontale, soggetto centrato · Formato JPG / PNG / WebP · max ~2&nbsp;MB
            </p>
            {form.cover_image_url && (
              <img src={mediaUrl(form.cover_image_url)} alt={form.cover_alt || "cover"} className="w-full aspect-video object-cover rounded-lg border border-[#2D3748] mb-3" />
            )}
            {form.cover_image_url && (
              <div className="mb-2">
                <label className={labelCls}>Testo alt copertina</label>
                <input data-testid="input-cover-alt" className={field} value={form.cover_alt || ""} onChange={(e) => set("cover_alt", e.target.value)} placeholder="Descrivi l'immagine includendo la keyword principale" />
                {!(form.cover_alt || "").trim() && curKeyword && (
                  <button type="button" data-testid="cover-alt-suggest" onClick={() => set("cover_alt", `${curTitle || selectedKeywords[0] || curKeyword} — ${selectedKeywords[0] || curKeyword}`)}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#06B6D4] hover:text-[#67e8f9] transition-colors">
                    <Tag className="w-3 h-3" /> Usa la keyword principale
                  </button>
                )}
              </div>
            )}
            <textarea data-testid="cover-brief" className={`${field} mb-2`} rows={2} value={form.cover_brief || ""} onChange={(e) => set("cover_brief", e.target.value)} placeholder="Descrivi che immagine vuoi (opzionale)" />
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="generate-cover-button" onClick={generateImages} disabled={genImages || genFree}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#8B5CF6] text-white text-sm font-semibold hover:bg-[#7C3AED] transition-colors disabled:opacity-60">
                {genImages ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{genImages ? "Generating…" : "Generate cover with AI"}
              </button>
              <button type="button" data-testid="generate-free-button" onClick={preparePollinations} disabled={genImages || genFree || buildingPrompt}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#06B6D4]/50 bg-[#06B6D4]/10 text-[#67e8f9] text-sm font-semibold hover:bg-[#06B6D4]/20 transition-colors disabled:opacity-60">
                {buildingPrompt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{buildingPrompt ? "Preparo…" : "Genera con AI (gratuito)"}
              </button>
              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#2D3748] bg-[#0B0D12] text-sm text-[#94A3B8] hover:text-white hover:border-[#8B5CF6] cursor-pointer transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{uploading ? "Uploading…" : "Upload"}
                <input data-testid="upload-cover" type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
            </div>
            {(genImages || genFree) && imageOptions.length === 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {[...Array(3)].map((_, i) => <div key={i} className="aspect-video rounded-lg bg-[#1A202C] border border-[#2D3748] animate-pulse" />)}
              </div>
            )}
            {pollPrompt !== null && (
              <div data-testid="pollinations-prompt-panel" className="mt-3 rounded-xl border border-[#06B6D4]/30 bg-[#06B6D4]/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-[#67e8f9]">Prompt immagine (modificabile)</label>
                  <button type="button" data-testid="regenerate-prompt-button" onClick={preparePollinations} disabled={buildingPrompt || genFree}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors disabled:opacity-60">
                    {buildingPrompt ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Rigenera prompt
                  </button>
                </div>
                <textarea data-testid="pollinations-prompt-input" className={`${field} font-mono text-xs`} rows={4} value={pollPrompt} onChange={(e) => setPollPrompt(e.target.value)} placeholder="Descrivi l'immagine che vuoi generare (in inglese per risultati migliori)" />
                <div className="flex flex-wrap gap-2 mt-2">
                  <button type="button" data-testid="generate-free-images-button" onClick={generateFree} disabled={genFree || buildingPrompt || !pollPrompt.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#06B6D4] text-[#0B0D12] text-sm font-bold hover:bg-[#22d3ee] transition-colors disabled:opacity-60">
                    {genFree ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}{genFree ? "Genero immagini…" : "Genera immagini"}
                  </button>
                  <button type="button" data-testid="cancel-prompt-button" onClick={() => setPollPrompt(null)} disabled={genFree}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#2D3748] bg-[#0B0D12] text-sm text-[#94A3B8] hover:text-white hover:border-[#8B5CF6] transition-colors disabled:opacity-60">
                    <X className="w-4 h-4" /> Annulla
                  </button>
                </div>
                {genFree && <p className="text-[11px] text-[#64748B] mt-2">Generazione gratuita tramite Pollinations.ai — può richiedere 15-60 secondi.</p>}
              </div>
            )}
            {imageOptions.length > 0 && (
              <div data-testid="cover-options" className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-[#94A3B8]">
                    {imageSource === "free" ? "Proposte AI (gratuite)" : "Proposte AI"} — clicca per scegliere:
                  </p>
                  <div className="flex items-center gap-3">
                    <button type="button" data-testid="regenerate-cover-button" onClick={imageSource === "free" ? generateFree : generateImages} disabled={genImages || genFree}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B5CF6] hover:text-[#a78bfa] transition-colors disabled:opacity-60">
                      <Sparkles className="w-3 h-3" /> Rigenera
                    </button>
                    <button type="button" data-testid="clear-cover-options-button" onClick={() => { setImageOptions([]); setImageSource(null); }}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#94A3B8] hover:text-white transition-colors">
                      <X className="w-3 h-3" /> Chiudi
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {imageOptions.map((url, i) => {
                    const active = form.cover_image_url === url;
                    return (
                      <button type="button" key={url} data-testid={`cover-option-${i}`} onClick={() => { set("cover_image_url", url); toast.success("Cover selected"); }}
                        className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${active ? "border-[#8B5CF6] ring-2 ring-[#8B5CF6]/40" : "border-[#2D3748] hover:border-[#8B5CF6]/60"}`}>
                        <img src={mediaUrl(url)} alt={`option ${i + 1}`} className="w-full h-full object-cover" />
                        {active && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#8B5CF6]" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className={`space-y-3 rounded-xl border p-4 transition-colors ${authorHi ? "border-[#8B5CF6] ring-2 ring-[#8B5CF6]/40" : "border-[#2D3748]"}`}>
            <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Scheda autore (EEAT)</span>
            <p className="text-[11px] text-[#64748B] -mt-1">Firma dell'articolo mostrata in fondo. Per Spectraloom lascia vuoto: userà "Team di sound engineer di Spectraloom".</p>
            <div className="flex items-center gap-3">
              {form.author_photo_url ? (
                <img src={mediaUrl(form.author_photo_url)} alt="autore" className="w-14 h-14 rounded-full object-cover border border-[#2D3748] shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#0B0D12] border border-[#2D3748] flex items-center justify-center text-[#475569] shrink-0"><Upload className="w-5 h-5" /></div>
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2D3748] bg-[#0B0D12] text-xs text-[#94A3B8] hover:text-white hover:border-[#8B5CF6] cursor-pointer transition-colors">
                {uploadingAuthor ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}{uploadingAuthor ? "Caricamento…" : "Foto autore"}
                <input data-testid="upload-author-photo" type="file" accept="image/*" className="hidden" onChange={uploadAuthorPhoto} />
              </label>
              {form.author_photo_url && (
                <button type="button" data-testid="remove-author-photo" onClick={() => set("author_photo_url", "")} className="text-xs text-[#94A3B8] hover:text-red-400">Rimuovi</button>
              )}
            </div>
            <div>
              <label className={labelCls}>Nome autore</label>
              <input data-testid="input-author-name" className={field} value={form.author_name || ""} onChange={(e) => set("author_name", e.target.value)} placeholder="Es. Dott. Mario Rossi" />
            </div>
            <div>
              <label className={labelCls}>Titolo / qualifica</label>
              <input data-testid="input-author-title" className={field} value={form.author_title || ""} onChange={(e) => set("author_title", e.target.value)} placeholder="Es. Fisioterapista · Consulente fiscale" />
            </div>
          </div>

          <div className="space-y-3 rounded-xl border border-[#2D3748] p-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Social embeds</span>
            <div>
              <label className={labelCls}>YouTube URL</label>
              <input data-testid="input-youtube" className={field} value={form.social_links.youtube || ""} onChange={(e) => setSocial("youtube", e.target.value)} placeholder="https://youtube.com/watch?v=…" />
            </div>
            <div>
              <label className={labelCls}>Vimeo URL</label>
              <input data-testid="input-vimeo" className={field} value={form.social_links.vimeo || ""} onChange={(e) => setSocial("vimeo", e.target.value)} placeholder="https://vimeo.com/123456789" />
            </div>
            <div>
              <label className={labelCls}>TikTok URL</label>
              <input data-testid="input-tiktok" className={field} value={form.social_links.tiktok || ""} onChange={(e) => setSocial("tiktok", e.target.value)} placeholder="https://tiktok.com/@user/video/…" />
            </div>
            <div>
              <label className={labelCls}>Instagram URL</label>
              <input data-testid="input-instagram" className={field} value={form.social_links.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} placeholder="https://instagram.com/p/…" />
            </div>
            <div>
              <label className={labelCls}>SoundCloud URL</label>
              <input data-testid="input-soundcloud" className={field} value={form.social_links.soundcloud || ""} onChange={(e) => setSocial("soundcloud", e.target.value)} placeholder="https://soundcloud.com/artist/track…" />
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-2">
            <button data-testid="save-draft-button" onClick={() => save(false)} disabled={saving} className="w-full px-4 py-3 rounded-lg border border-[#2D3748] bg-[#121620] text-white text-sm font-semibold hover:border-[#8B5CF6] transition-colors disabled:opacity-60">
              Save as draft
            </button>
            <button data-testid="publish-button" onClick={() => save(true)} disabled={saving} className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4] text-white text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60">
              {saving ? "Saving…" : "Publish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
